import { Lang } from '@/data/constants';

type Dict = Record<string, string>;

export const en: Dict = {
  // ---------------------------------------------------------------------
  // SEO / GEO / AEO programmatic pages (trade, county, city, money,
  // resource and hub templates). Single source of truth for that copy.
  // ---------------------------------------------------------------------
  'nav.home': 'Home',
  'seo.faqTitle': 'Frequently Asked Questions',
  'seo.commonQuestions': 'Common questions',
  'seo.overview': 'Overview',
  'seo.howWorks': 'How GigGone works',
  'seo.openProjects': 'Current {trade} project opportunities',
  'seo.recentProjects': 'Open projects in {place}',
  'seo.noProjects': 'No open projects match this filter. Try another trade or a nearby location.',

  'seo.seeAll': 'See all open projects',
  'seo.cityLocked': 'City unlocks after claim',
  'seo.tradeEyebrow': 'Trade',
  'seo.countyEyebrow': 'County',
  'seo.cityEyebrow': 'City',
  'seo.moneyEyebrow': 'Construction leads',
  'seo.resourceEyebrow': 'Resource Center',
  'seo.directoryEyebrow': 'Directory',
  'seo.aboutEyebrow': 'About',
  'seo.memberEyebrow': 'Memberships',
  'seo.faqEyebrow': 'Help',
  'seo.postEyebrow': 'Post a project',
  'seo.tradesTitle': 'Trades',
  'seo.resourcesTitle': 'Resource Center',
  'seo.leadsHub': 'Construction Leads',
  'seo.projectDirTitle': 'Project Directory',
  'seo.contractorDirTitle': 'Contractor Directory',
  'seo.factTrade': 'Trade',
  'seo.factArea': 'Area served',
  'seo.factPrice': 'Price per lead',
  'seo.factPost': 'Posting a project',
  'seo.factFree': 'Free',
  'seo.factCity': 'City',
  'seo.factCounty': 'County',
  'seo.factRegion': 'Region',
  'seo.factCities': 'Cities covered',
  'seo.factCounties': 'Counties covered',
  'seo.factTrades': 'Trades covered',
  'seo.factIncluded': 'Included leads / month',
  'seo.tradeOverview':
    'General contractors and homeowners post {trade} scopes with a budget range, target date and — where available — plans and permits. Subcontractors browse every posting for free and only spend an included lead when the project fits their crew and service area.',
  'seo.step1': 'A homeowner or general contractor posts a real project with scope, budget range and target date.',
  'seo.step2': 'Subcontractors browse the trade, project type, budget and state at no cost. City, ZIP, address and contacts stay locked.',
  'seo.step3': 'A subcontractor with an active membership claims the lead, consuming one included lead.',
  'seo.step4': 'City, ZIP, approved contact details and any attached plan documents unlock instantly for that subcontractor.',
  'seo.trust1': 'Flat price per lead — trade, city and budget never change it.',
  'seo.trust2': 'Real posted projects, not resold third-party lists.',
  'seo.trust3': 'Location and contacts stay private until a lead is claimed.',
  'seo.trust4': 'Full marketplace in English and Spanish.',
  'seo.leadPages': '{trade} leads by location',
  'seo.leadPagesPlace': 'Lead pages for {place}',
  'seo.byCity': 'Browse by city',
  'seo.byCounty': 'Browse by county',
  'seo.byTrade': 'Browse by trade',
  'seo.relatedTrades': 'Related trades',
  'seo.guides': 'Guides for this trade',
  'seo.relatedGuides': 'Related guides',
  'seo.relatedPages': 'Related pages',
  'seo.citiesIn': 'Cities in {place}',
  'seo.nearbyCounties': 'Nearby counties',
  'seo.nearbyCities': 'Nearby cities',
  'seo.allTrades': 'All construction trades',
  'seo.allCounties': 'All 58 California counties',
  'seo.topCities': 'Top California cities',
  'seo.activeTrades': 'Active trades in this county',
  'seo.popularTrades': 'Popular trades in {city}',
  'seo.opportunityOverview': 'Opportunity overview',
  'seo.constructionActivity': 'Construction activity',
  'seo.countyBody':
    '{county} sits in the {region} region with roughly {pop} residents. Work posted here ranges from single-trade residential repairs to commercial tenant improvements and ground-up builds, so subcontractors of every size find scopes that fit.',
  'seo.cityBody':
    'Projects posted in {city} are part of the {county} market. Homeowners typically post repair and remodel scopes, while general contractors post trade packages that need a licensed sub with current insurance.',
  'seo.whyBuy': 'Why buy {trade} leads in {place}',
  'seo.membershipOptions': 'Membership options',
  'seo.includedLeads': '{n} included leads every month',
  'seo.viewPlans': 'View plans',
  'seo.sameTradeOther': 'Same trade, other locations',
  'seo.otherTradesPlace': 'Other trades in {place}',
  'seo.ctaSubTitle': 'Get construction work without chasing it',
  'seo.ctaSubBody': 'Join as a subcontractor, pick your trades, and unlock projects for a flat {price} each.',
  'seo.ctaSubBtn': 'Join as a subcontractor',
  'seo.ctaPostTitle': 'Post your project free',
  'seo.ctaPostBody': 'Describe the work once. Licensed subcontractors in your area come to you.',
  'seo.ctaPostBtn': 'Post a project',
  'seo.ctaBrowse': 'Browse open projects',
  'seo.tradesHubIntro':
    'Every one of the {n} construction trades on the GigGone marketplace, grouped the way a jobsite is actually built — structure, envelope, mechanical, interiors, sitework and commercial management.',
  'seo.caHubIntro':
    'GigGone covers all {counties} California counties and {cities}+ cities. Pick a county or city to see the construction activity, the trades hiring, and the projects currently posted.',
  'seo.leadsHubIntro':
    'Construction leads for a flat {price} each across {n} trade-and-location pages. Every lead is a real project posted by a homeowner or general contractor.',
  'seo.resourcesHubIntro':
    '{n} practical guides for contractors — bidding, estimating, CSLB licensing, prevailing wage, contracts, safety, scheduling and lead generation.',
  'seo.projectDirIntro':
    'Every construction project on GigGone, organised by trade, county and city. Location and contact details stay masked until a subcontractor claims the lead.',
  'seo.contractorDirIntro':
    'Browse the California markets GigGone serves. Subcontractors and general contractors connect through posted projects rather than a scrapeable contact list.',
  'seo.privacyNoteTitle': 'Why we do not publish a contact list',
  'seo.privacyNoteBody':
    'Contractor phone numbers and emails are never published publicly. Contact details are exchanged only between the two parties on a specific project after a lead is claimed — that protects members from scraping and cold-call spam.',
  'seo.howGigGoneHelps': 'How GigGone helps',
  'seo.resourceHelpBody':
    'GigGone turns this guide into work: real projects posted by homeowners and general contractors, one flat price per lead, and location and contact details protected until you claim.',
  'seo.exploreHub': 'Explore the marketplace',
  'seo.aboutTitle': 'About GigGone',
  'seo.aboutIntro':
    'GigGone is a California construction marketplace. Homeowners and general contractors post real projects for free; licensed subcontractors unlock the ones that fit for one flat price per lead.',
  'seo.aboutMeta':
    'GigGone is a California construction marketplace connecting homeowners, general contractors and licensed subcontractors across 58 counties and 50 trades.',
  'seo.aboutMissionT': 'Why GigGone exists',
  'seo.aboutMissionB':
    'Subcontractors waste money on recycled lead lists and bidding platforms that charge per bid. Homeowners struggle to find a licensed sub who answers the phone. GigGone fixes both sides: posting is free, every lead costs the same flat price, and nobody pays to bid.',
  'seo.aboutHowT': 'How the marketplace works',
  'seo.aboutHowB':
    'A project is posted with its scope, trade, project type, budget range, target date and state. Those details are public. The exact city, ZIP, address, contact block, internal notes and plan documents stay locked until a subcontractor with an active membership claims the lead.',
  'seo.aboutPrivacyT': 'Privacy and lead protection',
  'seo.aboutPrivacyB':
    'Public pages never expose a project city, ZIP, address, contact name, phone, email, internal notes or document URLs. Documents are served only through short-lived signed links to the subcontractor who claimed that specific lead.',
  'seo.memberTitle': 'Subcontractor Memberships',
  'seo.memberIntro':
    'Memberships include a set number of leads every month. Claim a lead and the city, ZIP, contacts and plan documents unlock instantly.',
  'seo.memberMeta':
    'GigGone subcontractor memberships include monthly construction leads at a flat {price} per lead. Basic, Pro and Premium plans.',
  'seo.memberWhatT': 'What a claimed lead includes',
  'seo.memberWhatB':
    'Exact city and ZIP, the approved contact details for the poster, the full scope, and any attached plans, scopes, specs or permits — delivered through expiring signed links so the files cannot be shared or scraped.',
  'seo.term12': 'Billed monthly — 12-month commitment',
  'seo.termMonthly': 'Monthly — cancel anytime',
  'seo.gcTitle': 'For General Contractors',
  'seo.gcEyebrow': 'General contractors',
  'seo.gcIntro':
    'Post trade packages and get bids from licensed subcontractors who actually work in your area. Posting is free — you never pay to receive a bid.',
  'seo.gcMeta': 'General contractors post trade packages free on GigGone and receive bids from licensed California subcontractors.',
  'seo.gcQ': 'How do general contractors hire subcontractors on GigGone?',
  'seo.gcA':
    'A general contractor posts a work package with scope, trade, budget range, target date and optional plan documents. Licensed subcontractors claim the lead, see the full scope and contact details, and bid directly. Posting and receiving bids are free.',
  'seo.gcStep1': 'Post the work package with scope, budget range and target date, and attach plans if you have them.',
  'seo.gcStep2': 'Licensed subcontractors in your area claim the lead and see the scope, plans and your approved contact details.',
  'seo.gcStep3': 'You receive bids directly, verify license and insurance, and award the work.',
  'seo.gcCta': 'Post a work package',
  'seo.subTitle': 'For Subcontractors',
  'seo.subEyebrow': 'Subcontractors',
  'seo.subIntro':
    'Real projects, one flat price per lead, no pay-to-bid. Pick your trades and service area and unlock only the work you actually want.',
  'seo.subMeta': 'Subcontractors find California construction work on GigGone for a flat price per lead — no pay-to-bid, no recycled lists.',
  'seo.subQ': 'How do subcontractors find construction work on GigGone?',
  'seo.subA':
    'Subcontractors browse every posted project for free — trade, project type, budget range and state are all public. When a project fits, they claim it with an included lead from their membership, which unlocks the exact city, ZIP, contact details and plan documents.',
  'seo.subStep1': 'Create an account, select your trades and choose a membership with the number of leads you need.',
  'seo.subStep2': 'Browse posted projects free and check the budget range, project type and state before spending anything.',
  'seo.subStep3': 'Claim the leads you want — city, ZIP, contacts and plans unlock immediately and stay on your account.',
  'seo.subCta': 'Join as a subcontractor',
  'seo.hoTitle': 'For Homeowners',
  'seo.hoEyebrow': 'Homeowners',
  'seo.hoIntro':
    'Describe your project once and licensed contractors come to you. Posting is always free, and your address and phone number stay private until you are matched.',
  'seo.hoMeta': 'Homeowners post projects free on GigGone and get contacted by licensed California contractors.',
  'seo.hoQ': 'How do homeowners find contractors on GigGone?',
  'seo.hoA':
    'A homeowner posts the project with photos, a budget range and a target date. Licensed contractors in that area claim the lead and reach out directly. Posting is free and the homeowner\u2019s address, phone and email stay hidden from the public listing.',
  'seo.hoStep1': 'Post your project with photos, a budget range and when you want it done.',
  'seo.hoStep2': 'Licensed contractors in your area claim the lead and contact you directly.',
  'seo.hoStep3': 'Compare quotes, check licenses, and hire the contractor you trust.',
  'seo.hoCta': 'Post my project',
  'seo.postTitle': 'Post a Construction Project',
  'seo.postIntro':
    'Free for homeowners and general contractors. Describe the work, set a budget range, and licensed subcontractors in your area come to you.',
  'seo.postMeta': 'Post a construction project free on GigGone and get contacted by licensed California subcontractors.',
  'seo.postStep1': 'Describe the scope, pick the trade and set a budget range and target date.',
  'seo.postStep2': 'Add photos, plans or permits — plan documents stay private and are only released to the sub who claims the lead.',
  'seo.postStep3': 'Licensed subcontractors claim the lead and contact you directly. You never pay to receive a bid.',
  'seo.faqIntro': 'Straight answers about how the GigGone marketplace, leads, memberships and lead locking work.',
  'seo.faqMeta': 'Answers about GigGone construction leads, memberships, lead locking, posting projects and pricing.',
  'seo.helpTitle': 'Help Center',
  'seo.helpIntro': 'Answers, guides and next steps for homeowners, general contractors and subcontractors.',
  'seo.helpMeta': 'GigGone Help Center — guides and answers for homeowners, general contractors and subcontractors.',
  // nav / global
  'nav.jobs': 'Find Work',

  'nav.howItWorks': 'How It Works',
  'nav.pricing': 'Pricing',
  'nav.postProject': 'Post a Project',
  'nav.forContractors': 'For Contractors',
  'nav.login': 'Log In',
  'nav.signup': 'Sign Up',
  'nav.dashboard': 'Dashboard',
  'nav.logout': 'Log Out',
  'nav.admin': 'Admin',
  'nav.language': 'Language',
  'common.english': 'English',
  'common.spanish': 'Español',
  'common.loading': 'Loading...',
  'common.save': 'Save',
  'common.saving': 'Saving...',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.edit': 'Edit',
  'common.delete': 'Delete',
  'common.back': 'Back',
  'common.search': 'Search',
  'common.filters': 'Filters',
  'common.clear': 'Clear',
  'common.all': 'All',
  'common.required': 'Required',
  'common.optional': 'optional',
  'common.submit': 'Submit',
  'common.continue': 'Continue',
  'common.viewAll': 'View all',
  'common.none': 'None yet',
  'common.residential': 'Residential',
  'common.commercial': 'Commercial',
  'common.posted': 'Posted',
  'common.today': 'today',
  'common.daysAgo': 'days ago',
  'common.budget': 'Budget',
  'common.location': 'Location',
  'common.trade': 'Trade',
  'common.status': 'Status',
  'common.date': 'Date',
  'common.type': 'Type',
  'common.email': 'Email',
  'common.phone': 'Phone',
  'common.name': 'Name',
  'common.city': 'City',
  'common.state': 'State',
  'common.zip': 'Zip Code',
  'common.price': 'Price',
  'common.total': 'Total',
  'common.yes': 'Yes',
  'common.no': 'No',

  // home
  'home.heroBadge': 'Built for subcontractors',

  // --- Home page v2 (hero sequence, activity strip, trust pillars, CTA band) ---
  'home.heroTitleV2': 'Find construction work faster.',
  'home.heroSubV2':
    'Connect with homeowners and contractors who need licensed trades in your area. Every project is posted by a real account holder, and you only pay when you choose to unlock one.',

  'home.ctaBrowseProjects': 'Browse Projects',
  'home.ctaHowItWorks': 'How It Works',
  'home.statActiveProjects': 'Active projects',
  'home.statStates': 'States with open work',
  'home.statPostedValue': 'Posted budget value',
  'home.statsSource':
    'Every figure above is counted from projects currently open on this marketplace. We do not publish contractor counts or viewer counts we cannot substantiate.',
  'home.activityTitle': 'Marketplace at a glance',
  'home.activityAria': 'Marketplace at a glance',
  'home.activityTrades': 'Trades with open work',
  'home.activityHomeowner': 'Homeowner projects',
  'home.activityGc': 'Contractor work orders',
  'home.activityValue': 'Available work posted',

  'home.categoryTitle': 'Browse by trade',
  'home.categorySub': 'Jump straight to the work you actually do.',
  'home.trustBadge': 'Trust & transparency',
  'home.trustTitle': 'Built for contractors who need to know who they are dealing with',
  'home.trustSub':
    'We publish the things a contractor, a homeowner or an enterprise IT reviewer can check for themselves. No star ratings until we have a real review system, and no numbers we cannot back up.',
  'home.trustOperator': 'Operated by',
  'home.trustAddressTitle': 'A real, verifiable address',
  'home.trustAddressDesc':
    'Our operating company, street address and support email are published on every page and in machine-readable schema, not hidden behind a contact form.',
  'home.trustPrivacyTitle': 'Homeowner contact data stays private',
  'home.trustPrivacyDesc':
    'Public listings never expose a homeowner’s city, address, phone or email. Contact details are released only to the contractor who unlocks that specific project.',
  'home.trustPaymentTitle': 'Payments handled by a PCI-compliant processor',
  'home.trustPaymentDesc':
    'Card data is entered directly into our payment processor and never touches our servers. Lead pricing is fixed and shown before you pay.',
  'home.trustVerifyTitle': 'Verification badges mean something',
  'home.trustVerifyDesc':
    'Badges reflect checks we actually performed — email confirmation, account review and, where provided, license and insurance documentation. Unverified accounts are shown as unverified.',
  'home.trustSecurityTitle': 'Published security contact',
  'home.trustSecurityDesc':
    'We publish an RFC 9116 security.txt with a vulnerability reporting address, so researchers have a defined way to report issues instead of guessing.',
  'home.trustBilingualTitle': 'Fully bilingual platform',
  'home.trustBilingualDesc':
    'Every page, notice and legal document is available in English and Spanish, because the trades this marketplace serves are bilingual.',
  'home.bandTitle': 'Ready to grow your business?',
  'home.bandSub':
    'Get access to residential and commercial construction leads posted by homeowners and general contractors in your service area.',
  'home.bandPrice': 'Flat {price} per lead — no bidding, no surge pricing, no per-trade markup',
  'home.bandCta': 'Create Free Account',

  'home.heroTitle': 'Find construction work in your area. Today.',
  'home.heroSub': 'Homeowners post projects. General contractors post work orders. You unlock the ones you want and call the customer directly.',
  'home.ctaFindWork': 'See Available Jobs',
  'home.ctaPostProject': 'Post a Project — Free',
  'home.statJobs': 'Open jobs',
  'home.statTrades': 'Trades covered',
  'home.statResponse': 'Avg. response time',
  'home.statResponseVal': 'Under 5 min',
  'home.pathTitle': 'Choose how you use the platform',
  'home.pathSub': 'Three simple paths. No learning curve.',
  'home.homeownerTitle': 'I am a Homeowner',
  'home.homeownerDesc': 'Post your project once and get contacted by licensed pros near you. Always free.',
  'home.gcTitle': 'I am a General Contractor',
  'home.gcDesc': 'Post work orders and get subcontractors that actually show up. Free to post.',
  'home.subTitle': 'I am a Subcontractor',
  'home.subDesc': 'See every open job in your area. Unlock the ones you want, or subscribe for included leads.',
  'home.getStarted': 'Get Started',
  'home.latestJobs': 'Open opportunities',
  'home.latestJobsSub': 'Work orders and homeowner projects open in your region.',

  'home.howTitle': 'How it works',
  'home.how1Title': 'Create your free account',
  'home.how1Desc': 'Pick your role, add your trade and service area. Takes about 60 seconds.',
  'home.how2Title': 'See the work',
  'home.how2Desc': 'Available jobs load the moment you log in. Filter by trade, city, budget and job type.',
  'home.how3Title': 'Unlock and call',
  'home.how3Desc': 'Pay per lead or use your included subscription leads. You get the name, phone, email and full project details instantly.',
  'home.tradesTitle': 'Every trade on the jobsite',
  'home.tradesSub': 'Browse open work by trade and city.',
  'home.faqTitle': 'Frequently asked questions',
  'home.faq1Q': 'How much does a lead cost?',
  'home.faq1A': 'Every lead is the same flat $29 — the price never changes by trade, project type, budget or category. Members unlock leads from their included monthly allowance instead.',
  'home.faq2Q': 'Do I have to be a member?',
  'home.faq2A': 'No. You can buy single leads at $29 each with no membership. Basic ($99/mo, 5 leads) and Pro ($199/mo, 10 leads) are month-to-month. Premium is $499/mo with 20 leads every month and requires a 12-month commitment ($5,988 total, billed monthly — not upfront).',


  'home.faq3Q': 'Is it free for homeowners?',
  'home.faq3A': 'Yes. Posting a project is always free for homeowners, and general contractors post work orders for free as well.',
  'home.faq4Q': 'Is the platform available in Spanish?',
  'home.faq4A': 'Yes. The full platform, emails and text messages are available in English and Spanish. Switch languages any time from the header.',
  'home.ctaBottomTitle': 'There is open work in your area.',

  'home.ctaBottomSub': 'Create your free subcontractor account and see what is open in your city.',
  'home.newsletterTitle': 'Get new job alerts',
  'home.newsletterSub': 'We will send you new opportunities in your trade and city.',
  'home.newsletterOk': 'You are on the list. Watch your inbox for new jobs.',
  'home.smsOptIn': 'Text me new job alerts. Msg & data rates may apply. Reply STOP to unsubscribe.',
  'home.phonePlaceholder': 'Phone number (optional)',

  // jobs
  'jobs.title': 'Available Jobs',
  'jobs.subtitle': 'Open projects and work orders from homeowners and general contractors.',
  'jobs.filterTrade': 'Trade',
  'jobs.filterCity': 'City',
  'jobs.filterState': 'State',
  'jobs.filterRadius': 'Radius',
  'jobs.filterBudget': 'Minimum budget',
  'jobs.filterType': 'Project type',
  'jobs.results': 'jobs found',
  'jobs.empty': 'No projects match the selected filters. Try widening your search area.',
  'jobs.loading': 'Loading projects...',
  'jobs.errLoad': 'Projects could not be loaded. Please try again.',
  'jobs.errRate': 'Too many requests. Please wait and try again.',
  'jobs.errRateWait': 'Too many requests. Please try again in {minutes} minute(s).',
  'jobs.errNetwork': 'Check your connection and try again.',
  'jobs.retry': 'Retry',
  'jobs.clearFilters': 'Clear filters',
  'jobs.unlock': 'Unlock Lead',
  'jobs.unlocked': 'Unlocked',
  'jobs.cityHidden': 'City hidden until purchase',
  'jobs.unlockFailed': 'This lead could not be unlocked. Please try again or contact support.',

  'jobs.locationLockedTitle': 'Location available after lead purchase',
  'jobs.locationLockedBody': 'You can see the state, category, description and budget now. The city, exact address, ZIP code, contact details and project documents unlock as soon as you buy this lead.',

  'jobs.stateOnly': 'State only',
  'jobs.unlocksNow': 'Unlocked with your purchase',
  'jobs.documents': 'Project Documents',
  'jobs.documentsLocked': 'documents unlock after purchase',
  'docs.count': '{n} document(s) attached',
  'docs.drop': 'Attach PDF plans, blueprints, scope of work, permits or specifications',
  'docs.browse': 'Choose PDF files',
  'docs.limits': 'Up to {max} PDFs, {mb} MB each',
  'docs.errMax': 'You can attach up to {max} documents.',
  'docs.errType': 'Only PDF documents can be attached.',
  'docs.errSize': 'Each document must be smaller than {mb} MB.',
  'docs.kind.plans': 'Plans',
  'docs.kind.blueprints': 'Blueprints',
  'docs.kind.scope': 'Scope of Work',
  'docs.kind.permits': 'Permits',
  'docs.kind.specs': 'Specifications',
  'docs.kind.other': 'Other PDF',
  'docs.open': 'Open PDF',
  'docs.private': 'Private storage. Only verified buyers receive a temporary link.',
  'docs.errSignature': 'That file is not a real PDF and was rejected.',
  'docs.errLocked': 'That document could not be opened. Purchase the lead to get access.',
  'set.lockOffWarn': 'Warning: turning locking off makes city and ZIP visible to everyone in the public feed.',

  'form.documentsHint': 'Attach PDF plans, blueprints, scope of work, permits or specifications. Only subcontractors who purchase the lead can open them.',
  'form.errDocsRetry': 'Your attachments could not be saved. Your project can be published without files — remove the attachments and try again, or re-upload them.',
  'form.errDocsMax': 'You attached too many documents. Remove a few PDFs and try again.',
  'form.errDocsSize': 'One of the PDFs is larger than the allowed size. Upload a smaller file.',
  'form.errDocsInvalid': 'One of the attachments is not valid. Remove it and upload it again.',
  'form.errDocsDropped': 'Your project was published, but the documents were not saved. Edit the project and upload them again.',
  'form.errPhotosMax': 'You uploaded too many photos. Remove a few and try again.',
  'admin.piiFlag': 'Contact details exposed',
  'admin.piiBody': 'This listing has a phone number or email in its public text, which bypasses the lead lock. Edit or unpublish it.',
  'pricing.perTerm': ' / {n} months',

  'set.title': 'Project posting & lead locking',
  'set.sub': 'Control how many files a contractor can upload and what stays hidden before a lead is purchased.',
  'set.maxImages': 'Maximum images per project',
  'set.maxDocs': 'Maximum PDFs per project',
  'set.maxImageMb': 'Max image size (MB)',
  'set.maxDocMb': 'Max PDF size (MB)',
  'set.fileTypes': 'Allowed file types',
  'set.lockEnabled': 'Hide location until the lead is purchased',
  'set.lockFields': 'Fields hidden before purchase',
  'set.saved': 'Settings saved.',
  'set.saveErr': 'Could not save. Only administrators can change these settings.',
  'set.dbNote': 'City and ZIP are also protected in the database: the public feed is served from a masked view and the full row can only be read by the owner, a buyer, or an admin.',
  'admin.tabSettings': 'Settings',


  'jobs.viewDetails': 'View details',
  'jobs.urgent': 'Urgent',
  'jobs.typicalTimeline': 'Typical timeline',
  'jobs.verifiedPoster': 'Verified account',

  'jobs.postedBy': 'Posted by',
  'jobs.byHomeowner': 'Homeowner',
  'jobs.byGc': 'General Contractor',
  'jobs.distance': 'Distance',
  'jobs.miles': 'mi',
  'jobs.leadPrice': 'Lead price',
  'jobs.contactLocked': 'Contact details are hidden until you unlock this lead.',
  'jobs.contactUnlocked': 'Contact details',
  'jobs.startDate': 'Desired start',
  'jobs.dueDate': 'Due date',
  'jobs.description': 'Project description',
  'jobs.similar': 'Similar jobs nearby',
  'jobs.loginToUnlock': 'Log in as a subcontractor to unlock this lead.',
  'jobs.notFound': 'This job is no longer available.',

  // auth
  'auth.loginTitle': 'Log in to your account',
  'auth.loginSub': 'Welcome back. Your work is waiting.',
  'auth.password': 'Password',
  'auth.forgot': 'Forgot password?',
  'auth.reset': 'Send reset link',
  'auth.resetSent': 'If that email exists, a reset link is on the way.',
  'auth.noAccount': 'Do not have an account?',
  'auth.haveAccount': 'Already have an account?',
  'auth.chooseRole': 'What brings you here?',
  'auth.chooseRoleSub': 'Pick one. You can always create another account later.',
  'auth.registerHomeowner': 'Homeowner registration',
  'auth.registerGc': 'General contractor registration',
  'auth.registerSub': 'Subcontractor registration',
  'auth.createAccount': 'Create Account',
  'auth.creating': 'Creating account...',
  'auth.firstName': 'First Name',
  'auth.lastName': 'Last Name',
  'auth.companyName': 'Company Name',
  'auth.contactName': 'Contact Name',
  'auth.ownerName': 'Owner Name',
  'auth.license': 'License Number',
  'auth.years': 'Years in Business',
  'auth.employees': 'Number of Employees',
  'auth.serviceAreas': 'Service Areas (cities, comma separated)',
  'auth.tradesNeeded': 'Trade Categories Needed',
  'auth.tradeCategories': 'Trade Categories',
  'auth.selectTrades': 'Select every trade that applies',
  'auth.terms': 'By creating an account you agree to our Terms of Service and Privacy Policy.',
  'auth.errInvalid': 'Please complete all required fields.',
  'auth.errEmail': 'Enter a valid email address.',
  'auth.errPassword': 'Password must be at least 6 characters.',
  'auth.errCreds': 'Email or password is incorrect.',
  'auth.errTooMany': 'Too many attempts. Please try again in {minutes} minute(s).',
  'auth.errCaptcha': 'Security check failed. Please complete the verification and try again.',
  'auth.captchaLabel': 'Protected against automated abuse',
  'auth.captchaWait': 'Please complete the security check above.',

  'auth.errTrades': 'Select at least one trade.',
  'auth.preferredLanguage': 'Preferred Language',

  // dashboards
  'dash.welcome': 'Welcome back',
  'dash.myProjects': 'My Projects',
  'dash.postProject': 'Post Project',
  'dash.postWorkOrder': 'Post Work Order',
  'dash.projectResponses': 'Pro responses',
  'dash.noProjects': 'You have not posted a project yet.',
  'dash.noWorkOrders': 'You have not posted a work order yet.',
  'dash.myWorkOrders': 'My Work Orders',
  'dash.applicants': 'Applicants',
  'dash.availableJobs': 'Available Jobs',
  'dash.recommended': 'Recommended for you',
  'dash.recommendedSub': 'Matched to your trades and service area.',
  'dash.myLeads': 'My Purchased Leads',
  'dash.mySubscription': 'My Subscription',
  'dash.noLeads': 'You have not unlocked any leads yet. Unlock one above to get contact details instantly.',
  'dash.noSubscription': 'No active plan. You are on pay-per-lead.',
  'dash.viewPlans': 'View Plans',
  'leads.balanceTitle': 'Monthly Lead Balance',
  'leads.remaining': 'Leads left',
  'leads.usedOf': '{used} of {included} included leads used this month',
  'leads.renews': 'Renews',
  'leads.exhausted': 'You have used all of the leads included with your plan for this billing period. No new leads will be released to your account until it renews — upgrade your plan to unlock more now.',
  'leads.pastDue': 'Your last payment did not go through, so lead delivery is paused. Update your payment method to resume.',
  'leads.upgrade': 'Upgrade Plan',
  'leads.explain': 'One credit = one lead. Your balance drops by one every time you unlock a lead. Credits are for the current billing cycle only: unused credits are forfeited at the end of the cycle and do not roll over. Your next allotment is added as soon as your renewal payment is confirmed — if a payment does not go through, no new credits are issued.',

  'leads.noneLeft': 'No credits left this billing cycle. Buy a single lead for $29 or upgrade your plan.',
  'leads.extraCredits': 'Single-lead credits',
  'leads.extraCreditsNote': 'Purchased individually at $29 each. Used automatically once your plan credits for the cycle are gone.',
  'leads.noRollover': 'Credits do not roll over — unused credits are forfeited at renewal.',
  'leads.oneCreditNote': 'Unlocking this lead uses 1 credit.',
  'leads.soldOutTitle': 'Sold out',
  'leads.soldOutBody': 'This lead has been shared with the maximum of 6 contractors, so it is closed to new purchases. No credit was charged.',
  'leads.soldOutCta': 'Browse open projects',
  'leads.soldOutFlash': 'This lead just reached its 6-contractor limit. No credit was used.',

  'auth.newPassword': 'New password',
  'auth.confirmPassword': 'Confirm new password',
  'auth.currentPassword': 'Current password',
  'auth.changePassword': 'Change Password',
  'auth.passwordSaved': 'Your password has been updated.',
  'auth.errPasswordMatch': 'The two passwords do not match.',
  'auth.setNewTitle': 'Choose a new password',
  'auth.setNewSub': 'Enter a new password for your account.',
  'auth.setNewCta': 'Save new password',
  'auth.resetExpired': 'This reset link is invalid or has expired. Request a new one from the login page.',
  'auth.backToLogin': 'Back to log in',
  'dash.includedLeads': 'Included leads remaining',
  'dash.needTools': 'Need More Tools?',
  'dash.needToolsSub': 'Optional upgrades. Add them only if you want them.',
  'dash.addon.crm': 'CRM',
  'dash.addon.crmDesc': 'Contacts, pipelines, follow ups and tasks.',
  'dash.addon.crew': 'Crew Management',
  'dash.addon.crewDesc': 'Employee list, crew and job assignment, scheduling.',
  'dash.addon.chat': 'Project Chat',
  'dash.addon.chatDesc': 'Messaging, photos and file sharing.',
  'dash.addon.logs': 'Daily Logs',
  'dash.addon.logsDesc': 'Work logs, photo logs, material tracking, progress.',
  'dash.addonRequest': 'Request access',
  'dash.addonRequested': 'Request received. Our team will reach out.',
  'dash.leadStage': 'Stage',
  'dash.stage.purchased': 'Lead Purchased',
  'dash.stage.contacted': 'Contacted',
  'dash.stage.estimate': 'Estimate Sent',
  'dash.stage.won': 'Won',
  'dash.stage.lost': 'Lost',
  'dash.settings': 'Account Settings',

  // project / work order forms
  'form.projectTitle': 'Project Title',
  'form.projectDesc': 'Project Description',
  'form.category': 'Project Category',
  'form.address': 'Property Address',
  'form.budgetRange': 'Budget Range',
  'form.startDate': 'Desired Start Date',
  'form.photos': 'Upload Photos',
  'form.photosHint': 'Drag photos here, choose files, or take a photo with your phone. Optional but gets more responses.',
  'upload.drop': 'Drag and drop photos here',
  'upload.or': 'or',
  'upload.browse': 'Choose files',
  'upload.camera': 'Take photo',
  'upload.uploading': 'Uploading...',
  'upload.remove': 'Remove photo',
  'upload.count': '{n} of {max} photos',
  'upload.cover': 'Cover',
  'upload.makeCover': 'Make cover',
  'upload.moveLeft': 'Move left',
  'upload.moveRight': 'Move right',
  'dash.editProject': 'Edit',
  'form.editProject': 'Edit project',
  'form.editWorkOrder': 'Edit work order',
  'form.saveChanges': 'Save changes',

  'upload.errType': 'Only image files (JPG, PNG, WEBP, HEIC) can be uploaded.',
  'upload.errSize': 'Each photo must be smaller than 10 MB.',
  'upload.errMax': 'You can upload up to {max} photos.',
  'upload.errFailed': 'That upload did not finish. Please try again.',
  'upload.errAuth': 'Log in again to upload photos.',
  'gallery.prev': 'Previous photo',
  'gallery.next': 'Next photo',
  'gallery.photo': 'Photo',
  'gallery.swipeHint': 'Swipe to see more photos',

  'form.notes': 'Additional Notes',
  'form.woTitle': 'Work Order Title',
  'form.woDesc': 'Description',
  'form.tradeRequired': 'Trade Required',
  'form.dueDate': 'Due Date',
  'form.budget': 'Budget',
  'form.documents': 'Upload Documents',
  'form.projectType': 'Project Type',
  'form.publish': 'Publish',
  'form.publishing': 'Publishing...',
  'form.successProject': 'Your project is published. Licensed pros are being notified now.',
  'form.successWo': 'Your work order is live. Subcontractors in that trade are being notified.',
  'form.contactSection': 'How should pros reach you?',

  // checkout
  'pay.unlockTitle': 'Unlock this lead',
  'pay.unlockSub': 'One time payment. You get the full contact details immediately.',
  'pay.subscribeTitle': 'Start your plan',
  'pay.summary': 'Order summary',
  'pay.leadFor': 'Lead',
  'pay.payNow': 'Pay Now',
  'pay.processing': 'Processing...',
  'pay.success': 'Payment complete. Contact details unlocked.',
  'pay.successSub': 'Your subscription is active.',
  'pay.error': 'We could not start the payment. Please try again.',
  'pay.setup': 'Payments are being set up. Please check back shortly.',
  'pay.useIncluded': 'Use 1 included lead',
  'pay.usedIncluded': 'Lead unlocked using your plan credits.',
  'pay.securedBy': 'Secure encrypted payment',
  // ── $29 single lead purchase (inline card payment) ─────────────────────
  'jobs.unlockWithCredit': 'Unlock for 1 Credit',

  // ── Checkout return: automatic credit activation ───────────────────────
  'payret.title': 'Payment complete',
  'payret.paid': 'Payment successful',
  'payret.activating': 'Payment received. We are activating your credits…',
  'payret.waitBody': 'This takes a few seconds. Keep this page open — we will take you straight to Projects.',
  'payret.stillWorking': 'Still working. We confirm your payment with the processor before any credit is issued.',
  'payret.creditReady': 'lead credit ready to use',
  'payret.creditsReady': 'lead credits ready to use',
  'payret.sending': 'Taking you to the available Projects…',
  'payret.browse': 'Browse Projects',
  'payret.signinBody': 'Your payment is confirmed. Log in with the email you paid with and your credits are applied automatically.',
  'payret.signin': 'Log in and get my credits',
  'payret.thenProjects': 'Then we continue to Projects automatically.',
  'payret.signupBody': 'Your payment is confirmed. Create your password to activate the account and your credits are applied automatically.',
  'payret.activateAccount': 'Activate my account and get my credits',
  'payret.slowTitle': 'Your payment is safe',
  'payret.slowBody': 'The processor confirmation is taking longer than usual. Your credits are delivered the moment it arrives — you will not be charged again. Check again or message us.',
  'payret.checkAgain': 'Check again',
  'payret.webhookNote': 'Credits are issued only when the payment is verified with the processor, never because a browser reached this page.',
  'payret.dashboard': 'Go to my dashboard',
  'tour.replay': 'Show the quick guide',
  'tour.replayNote': '5 steps · how to use your credits and contact the customer',

  'pay.buyThisLead': 'Buy This Lead',

  'pay.buyThisLeadNote': 'One-off $29 payment. You get 1 credit and this lead unlocks immediately.',
  'pay.processingPayment': 'Processing payment…',
  'pay.unlockingLead': 'Payment approved — unlocking your lead…',
  'pay.openingCheckout': 'Opening secure checkout…',
  'pay.loadingForm': 'Loading secure payment form…',
  'pay.formNotReady': 'The payment form is still loading. Please tap Pay again in a moment.',
  'pay.intentFailed': 'We could not start the payment. Please try again.',
  'pay.declined': 'Your card was declined. Try another card or contact your bank.',
  'pay.notCompleted': 'The payment was not completed. Nothing was charged.',
  'pay.timeout': 'The payment is taking too long. Nothing has been charged twice — tap Retry.',
  'pay.networkError': 'Connection problem. Nothing was double charged — tap Retry.',
  'pay.settleRetry': 'Your payment went through. We are still unlocking the lead — tap Retry or reload this page.',
  'pay.noChargeOnDecline': 'A declined payment creates no charge, no credit and no lead access.',
  'pay.retry': 'Retry payment',
  'pay.tryAgain': 'Payment not completed. You can try again.',
  'pay.alreadyOwned': 'You already have access to this lead — the contact details are below.',
  'pay.soldOutNoCharge': 'This lead just reached its 6-contractor limit, so it cannot be purchased. You have not been charged.',
  'pay.successTitle': 'Payment approved — lead unlocked',
  'pay.successBody':
    'Your $29 payment issued 1 credit and that credit unlocked this lead. The contact name, phone, email and exact address are available right here, and this purchase used one of the 6 contractor slots.',
  // A $29 purchase = 1 credit in the balance. BUYING IS NOT SPENDING: the
  // credit is consumed later, when the contractor presses Unlock Lead, exactly
  // like membership credits (Basic 5 / Pro 10 / Premium 20 / Single 1).
  'pay.creditedTitle': 'Payment confirmed — 1 credit added to your balance',
  'pay.creditedBody':
    'Your $29 payment added 1 lead credit to your account. The credit is yours until you decide to use it, and it works on any open project — not just this one. Nothing has been unlocked yet.',
  'pay.creditsInBalance': 'Credits available',
  'pay.unlockNowCta': 'Unlock this project (1 credit)',
  'pay.saveCreditCta': 'Keep the credit and browse projects',
  'pay.addingCredit': 'Payment approved — adding your credit…',
  'pay.creditNote': 'One-off $29 payment. You get 1 credit and you choose which project to spend it on.',


  'pay.continueCheckout': 'Continue to secure checkout',
  'pay.pendingTitle': 'Finish your payment in the secure window',
  'pay.pendingBody':
    'We opened secure checkout in a new tab. Your plan and included leads activate automatically as soon as we confirm the payment — usually in under a minute. You can close this window.',
  'pay.reopenCheckout': 'Reopen secure checkout',
  'pay.seePlans': 'See plans',
  'pay.leadNeedsPlan': 'Leads are unlocked with the included leads in your monthly plan. Choose a plan to get started.',
  'pay.activationPending': 'We are confirming your payment. Your plan activates automatically once it clears.',
  'pay.activated': 'Payment confirmed. Your plan is active.',
  'pay.contactUs': 'Contact us',



  // pricing
  'pricing.title': 'Simple pricing for subcontractors',
  'pricing.sub': 'Pay only for the work you want, or subscribe and save on every lead.',
  'pricing.payPerLead': 'Single Lead Purchase',
  'pricing.payPerLeadDesc': 'No membership required. Unlock any lead for one flat price.',
  'pricing.payPerLeadPrice': '$29 per lead',
  'pricing.perLead': 'per lead',
  'pricing.fixedLeadNote': 'Every lead is $29 — the price never changes by trade, project type, budget or category.',
  'pricing.plans': 'Membership plans',
  'pricing.perMonth': '/mo',
  'pricing.choosePlan': 'Choose plan',
  'pricing.currentPlan': 'Current plan',
  'pricing.howLeadsWork': 'How leads work',
  'pricing.howLeads1': 'Every individual lead on the marketplace costs the same flat $29. No surge pricing, no premium leads, no category pricing.',
  'pricing.howLeads2': 'If your membership has included leads remaining, unlocking a lead deducts one from your balance instead of charging you.',
  'pricing.howLeads3': 'When your included leads run out you can keep working and unlock any lead for $29, or upgrade your membership at any time.',
  'pricing.contactSupport': 'Contact support about a lead',
  'pricing.leadPriceTable': 'Lead pricing',
  'pricing.tradeCol': 'Trade',
  'pricing.resCol': 'Residential',
  'pricing.comCol': 'Commercial',
  'pricing.freeNote': 'Homeowners and general contractors post for free. Always.',

  // --- Floating conversion actions (Start Now CTA + WhatsApp) ---
  'cta.startNow': 'Start Now',
  'cta.getLeads': 'Get Leads Now',
  'cta.viewPlans': 'View Plans',
  'cta.getStarted': 'Get Started',
  'cta.whatsapp': 'WhatsApp',
  'cta.whatsappAria': 'Chat with us on WhatsApp',
  'cta.plansTitle': 'Start getting leads today',
  'cta.plansSub': 'Buy one lead or join a membership. Every lead costs the same flat price.',
  'cta.plansHeading': 'Membership plans',
  'cta.singleLead': 'Single Lead',
  'cta.singleLeadDesc': 'No membership. Buy one credit and unlock any open project.',
  'cta.oneCredit': '1 credit',
  'cta.creditsPerCycle': '{n} lead credits per billing cycle',
  'cta.cancelAnytime': 'Monthly — cancel anytime.',
  'cta.creditRules':
    '1 credit unlocks 1 lead. Credits are issued when each payment is confirmed and do not roll over — unused credits are forfeited at the end of the billing cycle. Every lead is shared with a maximum of 6 contractors.',

  'cta.compareAll': 'Compare all plans and features',
  'cta.questions': 'Questions before you buy?',
  'cta.chatNow': 'Chat with us on WhatsApp',
  'cta.replyFast': 'We usually reply in minutes during business hours.',


  'status.active': 'Active',
  'status.removed': 'Removed',
  'status.paused': 'Paused',


  // admin
  'admin.title': 'Executive Dashboard',
  'admin.users': 'Users',
  'admin.homeowners': 'Homeowners',
  'admin.gcs': 'General Contractors',
  'admin.subs': 'Subcontractors',
  'admin.projects': 'Projects',
  'admin.workOrders': 'Work Orders',
  'admin.leadsSold': 'Leads Sold',
  'admin.revenue': 'Total Revenue',
  'admin.mrr': 'Monthly Recurring Revenue',
  'admin.conversion': 'Lead conversion rate',
  'admin.topTrades': 'Top performing trades',
  'admin.geo': 'Geographic breakdown',
  'admin.manageUsers': 'Manage users',
  'admin.managePricing': 'Lead pricing',
  'admin.approve': 'Approve',
  'admin.suspend': 'Suspend',
  'admin.suspended': 'Suspended',
  'admin.approved': 'Approved',
  'admin.pending': 'Pending',
  'admin.activity': 'Activity log',
  'admin.noAccess': 'This area is restricted to platform administrators.',
  'admin.savePrice': 'Save prices',
  'admin.priceSaved': 'Pricing updated.',

  // contact / support
  'nav.contact': 'Contact',
  'contact.title': 'Contact & Support',
  'contact.sub': 'Send us a message and our bilingual team replies the same business day.',
  'contact.formTitle': 'Send us a message',
  'contact.subject': 'Subject',
  'contact.subject.general': 'General question',
  'contact.subject.billing': 'Billing & payments',
  'contact.subject.support': 'Technical support',
  'contact.subject.report': 'Report a listing',
  'contact.message': 'Message',
  'contact.messagePlaceholder': 'Tell us how we can help...',
  'contact.send': 'Send message',
  'contact.sending': 'Sending...',
  'contact.successTitle': 'Message received',
  'contact.successBody': 'Thanks for reaching out. Our team will reply by email within one business day.',
  'contact.sendAnother': 'Send another message',
  'contact.errMessage': 'Please write a message of at least 10 characters.',
  'contact.errSave': 'We could not send your message. Please try again.',
  'contact.reachUs': 'Other ways to reach us',
  'contact.emailUs': 'Email us',
  'contact.callUs': 'Call us',
  'contact.hours': 'Support hours',
  'contact.hoursVal': 'Monday to Friday, 7:00 AM to 6:00 PM Pacific',
  'contact.responseTime': 'Typical reply in under 1 business day',
  'contact.faqLink': 'Before you write, check our frequently asked questions.',
  'contact.privacyNote': 'We use your details only to answer your message. Read our Privacy Policy.',
  'admin.inbox': 'Contact Inbox',
  'admin.inboxEmpty': 'No messages yet.',
  'admin.markResolved': 'Mark resolved',
  'admin.reopen': 'Reopen',
  'admin.new': 'New',
  'admin.resolved': 'Resolved',
  'admin.filterAll': 'All',
  'admin.filterNew': 'Unresolved',
  'admin.replyEmail': 'Reply by email',
  'admin.tabOverview': 'Overview',
  'admin.tabInbox': 'Inbox',
  'admin.tabListings': 'Listings & Images',
  'admin.tabRoles': 'Role audit',
  'admin.tabPayments': 'Payments',
  'admin.tabSecurity': 'Security',
  'sec.humanCheck': 'Quick security check — we verify this on our servers, never in your browser alone.',
  'sec.botFailed': 'We could not verify you are human. Please complete the security check and try again.',
  'sec.rateLimited': 'Too many attempts. Please wait a few minutes and try again.',
  'sec.consentMarketing': 'I agree to be contacted about my request and accept the',
  'sec.title': 'Security & lead capture',
  'sec.sub': 'Bot challenges, rate limiting, audit log, anonymous visitor tracking and the CRM retry queue.',
  'sec.refresh': 'Refresh',
  'sec.visitors': 'Anonymous visitors',
  'sec.identified': 'Identified (gave contact info)',
  'sec.blocked24': 'Blocked / warnings (24h)',
  'sec.queuePending': 'CRM retries pending',
  'sec.queueDead': 'CRM dead-letter',
  'sec.eventsTitle': 'Security audit log',
  'sec.eventsSub': 'Bot challenges, rate-limit blocks, CRM sync results and permission failures. No passwords, card data or secrets are ever logged.',
  'sec.queueTitle': 'CRM retry queue',
  'sec.queueSub': 'Any lead GoHighLevel could not accept is parked here and retried automatically with exponential backoff every 5 minutes.',
  'sec.visitorsTitle': 'Recent anonymous visitors',
  'sec.visitorsSub': 'First-party visitor id only — no fingerprinting. Contact details appear only after the person submits a form.',
  'sec.event': 'Event',
  'sec.severity': 'Severity',
  'sec.detail': 'Detail',
  'sec.when': 'When',
  'sec.attempts': 'Attempts',
  'sec.lastError': 'Last error',
  'sec.landing': 'Landing page',
  'sec.source': 'Source',
  'sec.pages': 'Pages',
  'sec.retryNow': 'Run retry worker now',
  'sec.retryDone': 'Retry worker finished',
  'sec.noEvents': 'No events recorded yet.',

  'admin.payTitle': 'Plan checkout links',
  'admin.paySub': 'Live status of every hosted checkout link. Green means subcontractors can subscribe to that plan right now.',
  'admin.payRecheck': 'Re-check links',
  'admin.payChecking': 'Checking links...',
  'admin.payReady': 'Checkout ready',
  'admin.payMissing': 'Checkout link missing',
  'admin.payOpenLink': 'Open checkout page',
  'admin.paySecret': 'Secret',
  'admin.payAllReady': 'All plan checkout links are configured. Every plan can be purchased.',
  'admin.paySomeMissing': '{n} plan checkout link(s) are missing. Those buttons cannot take a payment until the secret is set.',
  'admin.payFailTitle': 'Blocked subscription attempts',
  'admin.payFailSub': 'Subcontractors who tried to subscribe while a checkout link was unavailable. Follow up so the sale is not lost.',
  'admin.payFailEmpty': 'No blocked attempts. Every checkout request has reached a payment page.',
  'admin.payFailReason': 'Reason',
  'admin.payFailPlan': 'Plan',
  'admin.payFailWhen': 'Attempted',
  'admin.payReason.checkout_link_not_set': 'Checkout link not set',
  'admin.payReason.unknown_plan': 'Unknown plan',
  'admin.payReason.error': 'Checkout service error',
  'admin.payFollowUp': 'Email this contractor',
  'admin.payResolve': 'Mark followed up',
  'admin.payResolved': 'Followed up',
  'admin.payFailOpen': 'Needs follow-up',

  'admin.moderationTitle': 'Listings & images moderation',
  'admin.moderationSub': 'Remove an individual photo or unpublish a listing. Every action is logged.',
  'admin.modSearch': 'Search by title, city, state, trade or poster email',
  'admin.modFilterAll': 'All',
  'admin.modFilterPublished': 'Published',
  'admin.modFilterRemoved': 'Removed',
  'admin.modSelectAll': 'Select all shown',
  'admin.modSelected': '{n} selected',
  'admin.modBulkUnpublish': 'Unpublish selected',
  'admin.modBulkDone': '{n} listing(s) removed from the homepage and marketplace.',
  'admin.modOwner': 'Posted by',
  'admin.modNoMatch': 'No listing matches that search.',
  'admin.modHint':
    'Unpublishing a listing removes it from the homepage, the marketplace and search immediately. Nothing is deleted — you can restore it here at any time.',
  'admin.modShowing': 'Showing {n} of {total}',
  // Status badge label on a card (singular, unlike the plural filter buttons).
  'admin.modStatusPublished': 'Published',
  'admin.modStatusRemoved': 'Removed',


  'admin.photos': 'photos',
  'admin.unpublish': 'Unpublish',
  'admin.restoreListing': 'Restore',
  'admin.removePhoto': 'Remove photo',
  'admin.roleAuditTitle': 'Role audit',
  'admin.roleAuditSub': 'Every account grouped by type. Correcting a role updates the profile and the login metadata.',
  'admin.makeRole': 'Set as',
  'admin.roleFixed': 'Role updated.',
  'admin.roleFixError': 'Could not update that role.',
  'admin.createSubTitle': 'Create subcontractor account',
  'admin.createSubSub': 'Creates a real subcontractor login and marketplace profile. Only administrators can use this form.',
  'admin.createSubBtn': 'Create subcontractor',
  'admin.createSubCreating': 'Creating account...',
  'admin.createSubOk': 'Subcontractor account created for {email}.',
  'admin.createSubPasswordHint': 'At least 10 characters. Share it with the account holder securely.',
  'admin.createSubErrExists': 'An account with that email already exists.',
  'admin.createSubErrPassword': 'Password must be at least 10 characters.',
  'admin.createSubErrForbidden': 'Only administrators can create accounts here.',
  'admin.createSubErrGeneric': 'The account could not be created. Please try again.',

  'photos.cover': 'Cover',
  'cover.category': 'Category photo',
  'cover.categoryNote':
    'The homeowner did not upload photos for this project. The image shown is a reference photo for this category, not the actual project.',

  'role.homeowner': 'Homeowner',
  'role.gc': 'General Contractor',
  'role.subcontractor': 'Subcontractor',
  'verify.verified': 'Verified',
  'verify.licensed': 'Licensed',
  'verify.insured': 'Insured',
  'home.tradesContact': 'Do not see your trade? Email us at',
  'dash.responsesTitle': 'Who responded to your projects',
  'dash.responsesSub': 'Pros who opened your project and can contact you.',
  'dash.noResponses': 'No pro has responded yet. You will see them here as soon as they do.',
  'dash.contractorInterested': 'Interested pro',
  'dash.respondedOn': 'Responded',


  // legal / footer
  'footer.company': 'Company',
  'footer.about': 'About',
  'footer.contact': 'Contact',
  'footer.aboutUs': 'About Us',
  'footer.contactUs': 'Contact Us',
  'footer.trustNav': 'Company & legal',
  'footer.emailUs': 'Business email',
  'footer.address': 'Business address',
  'contact.mailingAddress': 'Business & mailing address',
  'contact.addressNote': 'Registered business address. Mail sent here reaches the GigGone team.',

  'seo.aboutContactT': 'Company information and contact',
  'seo.aboutContactB':
    'GigGone Marketplace operates the giggone.com construction marketplace. For any question about the platform, your account, billing, a listing or a privacy request, email our team — we reply within one business day.',
  'seo.aboutTrustT': 'Policies and company pages',


  'footer.resources': 'Resources',
  'footer.forHomeowners': 'For Homeowners',
  'footer.forGcs': 'For General Contractors',
  'footer.forSubs': 'For Subcontractors',
  'footer.legal': 'Legal',
  'footer.privacy': 'Privacy Policy',
  'footer.terms': 'Terms of Service',
  'footer.cookies': 'Cookie Policy',
  'footer.rights': 'All rights reserved.',
  'footer.builtFor': 'Licensed trades. Real work. No noise.',
  'cookie.text': 'We use cookies to keep you logged in, remember your language and measure platform performance.',
  'cookie.accept': 'Accept',
  'cookie.decline': 'Decline',
  'legal.privacyTitle': 'Privacy Policy',
  'legal.termsTitle': 'Terms of Service',
  'legal.updated': 'Last updated',
};

export const es: Dict = {
  // --- SEO / GEO / AEO programmatic pages (Spanish) ---
  'nav.home': 'Inicio',
  'seo.faqTitle': 'Preguntas Frecuentes',
  'seo.commonQuestions': 'Preguntas comunes',
  'seo.overview': 'Resumen',
  'seo.howWorks': 'Cómo funciona GigGone',
  'seo.openProjects': 'Proyectos abiertos de {trade}',
  'seo.recentProjects': 'Proyectos abiertos en {place}',
  'seo.noProjects': 'Ningún proyecto abierto coincide con este filtro. Pruebe otro oficio o una ubicación cercana.',

  'seo.seeAll': 'Ver todos los proyectos abiertos',
  'seo.cityLocked': 'La ciudad se desbloquea al reclamar',
  'seo.tradeEyebrow': 'Oficio',
  'seo.countyEyebrow': 'Condado',
  'seo.cityEyebrow': 'Ciudad',
  'seo.moneyEyebrow': 'Leads de construcción',
  'seo.resourceEyebrow': 'Centro de Recursos',
  'seo.directoryEyebrow': 'Directorio',
  'seo.aboutEyebrow': 'Acerca de',
  'seo.memberEyebrow': 'Membresías',
  'seo.faqEyebrow': 'Ayuda',
  'seo.postEyebrow': 'Publicar proyecto',
  'seo.tradesTitle': 'Oficios',
  'seo.resourcesTitle': 'Centro de Recursos',
  'seo.leadsHub': 'Leads de Construcción',
  'seo.projectDirTitle': 'Directorio de Proyectos',
  'seo.contractorDirTitle': 'Directorio de Contratistas',
  'seo.factTrade': 'Oficio',
  'seo.factArea': 'Área de servicio',
  'seo.factPrice': 'Precio por lead',
  'seo.factPost': 'Publicar un proyecto',
  'seo.factFree': 'Gratis',
  'seo.factCity': 'Ciudad',
  'seo.factCounty': 'Condado',
  'seo.factRegion': 'Región',
  'seo.factCities': 'Ciudades cubiertas',
  'seo.factCounties': 'Condados cubiertos',
  'seo.factTrades': 'Oficios cubiertos',
  'seo.factIncluded': 'Leads incluidos / mes',
  'seo.tradeOverview':
    'Los contratistas generales y los propietarios publican alcances de {trade} con rango de presupuesto, fecha estimada y, cuando existen, planos y permisos. Los subcontratistas revisan todas las publicaciones gratis y solo usan un lead incluido cuando el proyecto le conviene a su cuadrilla.',
  'seo.step1': 'Un propietario o contratista general publica un proyecto real con alcance, presupuesto y fecha.',
  'seo.step2': 'Los subcontratistas ven el oficio, el tipo de proyecto, el presupuesto y el estado sin costo. La ciudad, el código postal, la dirección y los contactos quedan bloqueados.',
  'seo.step3': 'Un subcontratista con membresía activa reclama el lead y consume un lead incluido.',
  'seo.step4': 'La ciudad, el código postal, los contactos aprobados y los documentos adjuntos se desbloquean al instante.',
  'seo.trust1': 'Precio fijo por lead — el oficio, la ciudad y el presupuesto nunca lo cambian.',
  'seo.trust2': 'Proyectos reales publicados, no listas revendidas de terceros.',
  'seo.trust3': 'La ubicación y los contactos permanecen privados hasta que se reclama el lead.',
  'seo.trust4': 'Marketplace completo en inglés y español.',
  'seo.leadPages': 'Leads de {trade} por ubicación',
  'seo.leadPagesPlace': 'Páginas de leads para {place}',
  'seo.byCity': 'Explorar por ciudad',
  'seo.byCounty': 'Explorar por condado',
  'seo.byTrade': 'Explorar por oficio',
  'seo.relatedTrades': 'Oficios relacionados',
  'seo.guides': 'Guías para este oficio',
  'seo.relatedGuides': 'Guías relacionadas',
  'seo.relatedPages': 'Páginas relacionadas',
  'seo.citiesIn': 'Ciudades en {place}',
  'seo.nearbyCounties': 'Condados cercanos',
  'seo.nearbyCities': 'Ciudades cercanas',
  'seo.allTrades': 'Todos los oficios de construcción',
  'seo.allCounties': 'Los 58 condados de California',
  'seo.topCities': 'Principales ciudades de California',
  'seo.activeTrades': 'Oficios activos en este condado',
  'seo.popularTrades': 'Oficios populares en {city}',
  'seo.opportunityOverview': 'Panorama de oportunidades',
  'seo.constructionActivity': 'Actividad de construcción',
  'seo.countyBody':
    '{county} está en la región {region} con aproximadamente {pop} habitantes. El trabajo publicado va desde reparaciones residenciales de un solo oficio hasta mejoras comerciales y obra nueva, así que hay alcances para cuadrillas de todo tamaño.',
  'seo.cityBody':
    'Los proyectos publicados en {city} pertenecen al mercado de {county}. Los propietarios publican reparaciones y remodelaciones, mientras que los contratistas generales publican paquetes de trabajo que requieren un subcontratista con licencia y seguro vigente.',
  'seo.whyBuy': 'Por qué comprar leads de {trade} en {place}',
  'seo.membershipOptions': 'Opciones de membresía',
  'seo.includedLeads': '{n} leads incluidos cada mes',
  'seo.viewPlans': 'Ver planes',
  'seo.sameTradeOther': 'Mismo oficio, otras ubicaciones',
  'seo.otherTradesPlace': 'Otros oficios en {place}',
  'seo.ctaSubTitle': 'Consiga trabajo de construcción sin perseguirlo',
  'seo.ctaSubBody': 'Únase como subcontratista, elija sus oficios y desbloquee proyectos por {price} cada uno.',
  'seo.ctaSubBtn': 'Unirme como subcontratista',
  'seo.ctaPostTitle': 'Publique su proyecto gratis',
  'seo.ctaPostBody': 'Describa el trabajo una vez. Los subcontratistas con licencia de su área lo contactan.',
  'seo.ctaPostBtn': 'Publicar un proyecto',
  'seo.ctaBrowse': 'Ver proyectos abiertos',
  'seo.tradesHubIntro':
    'Los {n} oficios de construcción del marketplace GigGone, agrupados como realmente se construye una obra: estructura, envolvente, instalaciones, interiores, obra exterior y gerencia comercial.',
  'seo.caHubIntro':
    'GigGone cubre los {counties} condados de California y más de {cities} ciudades. Elija un condado o una ciudad para ver la actividad de construcción, los oficios que contratan y los proyectos publicados.',
  'seo.leadsHubIntro':
    'Leads de construcción por {price} cada uno en {n} páginas de oficio y ubicación. Cada lead es un proyecto real publicado por un propietario o contratista general.',
  'seo.resourcesHubIntro':
    '{n} guías prácticas para contratistas: licitación, estimación, licencias CSLB, salario prevaleciente, contratos, seguridad, programación y generación de leads.',
  'seo.projectDirIntro':
    'Todos los proyectos de GigGone, organizados por oficio, condado y ciudad. La ubicación y los contactos permanecen enmascarados hasta que un subcontratista reclama el lead.',
  'seo.contractorDirIntro':
    'Explore los mercados de California que atiende GigGone. Los subcontratistas y contratistas generales se conectan a través de proyectos publicados, no de una lista de contactos extraíble.',
  'seo.privacyNoteTitle': 'Por qué no publicamos una lista de contactos',
  'seo.privacyNoteBody':
    'Los teléfonos y correos de los contratistas nunca se publican. Los datos de contacto se intercambian solo entre las dos partes de un proyecto específico después de reclamar el lead — eso protege a los miembros del scraping y de las llamadas no solicitadas.',
  'seo.howGigGoneHelps': 'Cómo ayuda GigGone',
  'seo.resourceHelpBody':
    'GigGone convierte esta guía en trabajo: proyectos reales publicados por propietarios y contratistas generales, un precio fijo por lead, y ubicación y contactos protegidos hasta que usted reclame.',
  'seo.exploreHub': 'Explorar el marketplace',
  'seo.aboutTitle': 'Acerca de GigGone',
  'seo.aboutIntro':
    'GigGone es un marketplace de construcción en California. Los propietarios y contratistas generales publican proyectos reales gratis; los subcontratistas con licencia desbloquean los que les convienen por un precio fijo por lead.',
  'seo.aboutMeta':
    'GigGone es un marketplace de construcción en California que conecta propietarios, contratistas generales y subcontratistas con licencia en 58 condados y 50 oficios.',
  'seo.aboutMissionT': 'Por qué existe GigGone',
  'seo.aboutMissionB':
    'Los subcontratistas gastan dinero en listas de leads recicladas y plataformas que cobran por cotizar. Los propietarios no encuentran un subcontratista con licencia que conteste el teléfono. GigGone arregla ambos lados: publicar es gratis, cada lead cuesta lo mismo y nadie paga por cotizar.',
  'seo.aboutHowT': 'Cómo funciona el marketplace',
  'seo.aboutHowB':
    'Un proyecto se publica con su alcance, oficio, tipo, rango de presupuesto, fecha y estado. Esos datos son públicos. La ciudad exacta, el código postal, la dirección, los contactos, las notas internas y los planos permanecen bloqueados hasta que un subcontratista con membresía activa reclama el lead.',
  'seo.aboutPrivacyT': 'Privacidad y protección de leads',
  'seo.aboutPrivacyB':
    'Las páginas públicas nunca exponen la ciudad, el código postal, la dirección, el nombre de contacto, el teléfono, el correo, las notas internas ni las rutas de documentos. Los documentos se entregan solo con enlaces firmados de corta duración al subcontratista que reclamó ese lead.',
  'seo.memberTitle': 'Membresías para Subcontratistas',
  'seo.memberIntro':
    'Las membresías incluyen una cantidad fija de leads cada mes. Al reclamar un lead se desbloquean la ciudad, el código postal, los contactos y los planos.',
  'seo.memberMeta':
    'Las membresías de GigGone incluyen leads mensuales de construcción con un precio fijo de {price} por lead. Planes Basic, Pro y Premium.',
  'seo.memberWhatT': 'Qué incluye un lead reclamado',
  'seo.memberWhatB':
    'Ciudad y código postal exactos, los datos de contacto aprobados, el alcance completo y cualquier plano, alcance, especificación o permiso adjunto — entregados con enlaces firmados que expiran para que los archivos no se compartan ni se extraigan.',
  'seo.term12': 'Cobro mensual — compromiso de 12 meses',
  'seo.termMonthly': 'Mensual — cancele cuando quiera',
  'seo.gcTitle': 'Para Contratistas Generales',
  'seo.gcEyebrow': 'Contratistas generales',
  'seo.gcIntro':
    'Publique paquetes de trabajo y reciba cotizaciones de subcontratistas con licencia que realmente trabajan en su área. Publicar es gratis — nunca paga por recibir una cotización.',
  'seo.gcMeta': 'Los contratistas generales publican paquetes de trabajo gratis en GigGone y reciben cotizaciones de subcontratistas con licencia en California.',
  'seo.gcQ': '¿Cómo contratan subcontratistas los contratistas generales en GigGone?',
  'seo.gcA':
    'El contratista general publica un paquete de trabajo con alcance, oficio, presupuesto, fecha y planos opcionales. Los subcontratistas con licencia reclaman el lead, ven el alcance completo y los contactos, y cotizan directamente. Publicar y recibir cotizaciones es gratis.',
  'seo.gcStep1': 'Publique el paquete con alcance, presupuesto y fecha, y adjunte planos si los tiene.',
  'seo.gcStep2': 'Los subcontratistas con licencia de su área reclaman el lead y ven el alcance, los planos y sus contactos aprobados.',
  'seo.gcStep3': 'Recibe cotizaciones directas, verifica licencia y seguro, y adjudica el trabajo.',
  'seo.gcCta': 'Publicar un paquete de trabajo',
  'seo.subTitle': 'Para Subcontratistas',
  'seo.subEyebrow': 'Subcontratistas',
  'seo.subIntro':
    'Proyectos reales, un precio fijo por lead, sin pagar por cotizar. Elija sus oficios y su área y desbloquee solo el trabajo que quiere.',
  'seo.subMeta': 'Los subcontratistas encuentran trabajo de construcción en California por un precio fijo por lead — sin pagar por cotizar ni listas recicladas.',
  'seo.subQ': '¿Cómo encuentran trabajo los subcontratistas en GigGone?',
  'seo.subA':
    'Los subcontratistas revisan gratis todos los proyectos publicados — el oficio, el tipo de proyecto, el presupuesto y el estado son públicos. Cuando un proyecto les conviene, lo reclaman con un lead incluido de su membresía, lo que desbloquea la ciudad exacta, el código postal, los contactos y los planos.',
  'seo.subStep1': 'Cree una cuenta, seleccione sus oficios y elija una membresía con los leads que necesita.',
  'seo.subStep2': 'Revise gratis los proyectos publicados y verifique presupuesto, tipo y estado antes de gastar nada.',
  'seo.subStep3': 'Reclame los leads que quiera — la ciudad, el código postal, los contactos y los planos se desbloquean de inmediato y quedan en su cuenta.',
  'seo.subCta': 'Unirme como subcontratista',
  'seo.hoTitle': 'Para Propietarios',
  'seo.hoEyebrow': 'Propietarios',
  'seo.hoIntro':
    'Describa su proyecto una vez y los contratistas con licencia lo contactan. Publicar siempre es gratis, y su dirección y teléfono permanecen privados.',
  'seo.hoMeta': 'Los propietarios publican proyectos gratis en GigGone y reciben contacto de contratistas con licencia en California.',
  'seo.hoQ': '¿Cómo encuentran contratistas los propietarios en GigGone?',
  'seo.hoA':
    'El propietario publica el proyecto con fotos, rango de presupuesto y fecha deseada. Los contratistas con licencia de esa área reclaman el lead y lo contactan directamente. Publicar es gratis y la dirección, el teléfono y el correo quedan ocultos en la publicación pública.',
  'seo.hoStep1': 'Publique su proyecto con fotos, rango de presupuesto y cuándo lo quiere hecho.',
  'seo.hoStep2': 'Los contratistas con licencia de su área reclaman el lead y lo contactan directamente.',
  'seo.hoStep3': 'Compare cotizaciones, verifique licencias y contrate al contratista que le dé confianza.',
  'seo.hoCta': 'Publicar mi proyecto',
  'seo.postTitle': 'Publicar un Proyecto de Construcción',
  'seo.postIntro':
    'Gratis para propietarios y contratistas generales. Describa el trabajo, fije un rango de presupuesto y los subcontratistas con licencia de su área lo contactan.',
  'seo.postMeta': 'Publique un proyecto de construcción gratis en GigGone y reciba contacto de subcontratistas con licencia en California.',
  'seo.postStep1': 'Describa el alcance, elija el oficio y fije un rango de presupuesto y una fecha.',
  'seo.postStep2': 'Agregue fotos, planos o permisos — los documentos permanecen privados y solo se entregan al subcontratista que reclama el lead.',
  'seo.postStep3': 'Los subcontratistas con licencia reclaman el lead y lo contactan directamente. Nunca paga por recibir una cotización.',
  'seo.faqIntro': 'Respuestas directas sobre el marketplace, los leads, las membresías y el bloqueo de ubicación.',
  'seo.faqMeta': 'Respuestas sobre leads de construcción, membresías, bloqueo de leads, publicaciones y precios en GigGone.',
  'seo.helpTitle': 'Centro de Ayuda',
  'seo.helpIntro': 'Respuestas, guías y próximos pasos para propietarios, contratistas generales y subcontratistas.',
  'seo.helpMeta': 'Centro de Ayuda de GigGone — guías y respuestas para propietarios, contratistas generales y subcontratistas.',
  'nav.jobs': 'Buscar Trabajo',

  'nav.howItWorks': 'Cómo Funciona',
  'nav.pricing': 'Precios',
  'nav.postProject': 'Publicar Proyecto',
  'nav.forContractors': 'Para Contratistas',
  'nav.login': 'Iniciar Sesión',
  'nav.signup': 'Registrarse',
  'nav.dashboard': 'Panel',
  'nav.logout': 'Cerrar Sesión',
  'nav.admin': 'Administrador',
  'nav.language': 'Idioma',
  'common.english': 'English',
  'common.spanish': 'Español',
  'common.loading': 'Cargando...',
  'common.save': 'Guardar',
  'common.saving': 'Guardando...',
  'common.cancel': 'Cancelar',
  'common.close': 'Cerrar',
  'common.edit': 'Editar',
  'common.delete': 'Eliminar',
  'common.back': 'Regresar',
  'common.search': 'Buscar',
  'common.filters': 'Filtros',
  'common.clear': 'Limpiar',
  'common.all': 'Todos',
  'common.required': 'Obligatorio',
  'common.optional': 'opcional',
  'common.submit': 'Enviar',
  'common.continue': 'Continuar',
  'common.viewAll': 'Ver todo',
  'common.none': 'Nada todavía',
  'common.residential': 'Residencial',
  'common.commercial': 'Comercial',
  'common.posted': 'Publicado',
  'common.today': 'hoy',
  'common.daysAgo': 'días',
  'common.budget': 'Presupuesto',
  'common.location': 'Ubicación',
  'common.trade': 'Oficio',
  'common.status': 'Estado',
  'common.date': 'Fecha',
  'common.type': 'Tipo',
  'common.email': 'Correo Electrónico',
  'common.phone': 'Teléfono',
  'common.name': 'Nombre',
  'common.city': 'Ciudad',
  'common.state': 'Estado',
  'common.zip': 'Código Postal',
  'common.price': 'Precio',
  'common.total': 'Total',
  'common.yes': 'Sí',
  'common.no': 'No',

  'home.heroBadge': 'Hecho para subcontratistas',

  // --- Home v2 (secuencia del hero, actividad, confianza, banda CTA) ---
  'home.heroTitleV2': 'Encuentre trabajo de construcción más rápido.',
  'home.heroSubV2':
    'Conéctese con dueños de casa y contratistas que necesitan oficios con licencia en su área. Cada proyecto lo publica un titular de cuenta real, y usted solo paga cuando decide desbloquear uno.',

  'home.ctaBrowseProjects': 'Ver Proyectos',
  'home.ctaHowItWorks': 'Cómo Funciona',
  'home.statActiveProjects': 'Proyectos activos',
  'home.statStates': 'Estados con trabajo abierto',
  'home.statPostedValue': 'Presupuesto publicado',
  'home.statsSource':
    'Cada cifra de arriba se cuenta con los proyectos que están abiertos ahora en este marketplace. No publicamos cantidades de contratistas ni de visitantes que no podamos comprobar.',
  'home.activityTitle': 'Resumen del marketplace',
  'home.activityAria': 'Resumen del marketplace',
  'home.activityTrades': 'Oficios con trabajo abierto',
  'home.activityHomeowner': 'Proyectos de dueños de casa',
  'home.activityGc': 'Órdenes de contratistas',
  'home.activityValue': 'Trabajo disponible publicado',

  'home.categoryTitle': 'Explorar por oficio',
  'home.categorySub': 'Vaya directo al trabajo que usted realmente hace.',
  'home.trustBadge': 'Confianza y transparencia',
  'home.trustTitle': 'Hecho para contratistas que necesitan saber con quién tratan',
  'home.trustSub':
    'Publicamos lo que un contratista, un dueño de casa o un revisor de TI puede comprobar por su cuenta. Sin calificaciones de estrellas hasta que exista un sistema real de reseñas, y sin cifras que no podamos respaldar.',
  'home.trustOperator': 'Operado por',
  'home.trustAddressTitle': 'Una dirección real y verificable',
  'home.trustAddressDesc':
    'Nuestra empresa, dirección física y correo de soporte se publican en cada página y en datos estructurados legibles por máquina, no escondidos detrás de un formulario.',
  'home.trustPrivacyTitle': 'Los datos del dueño de casa son privados',
  'home.trustPrivacyDesc':
    'Las publicaciones públicas nunca muestran la ciudad, dirección, teléfono ni correo del dueño de casa. Los datos de contacto se entregan solo al contratista que desbloquea ese proyecto.',
  'home.trustPaymentTitle': 'Pagos con un procesador certificado PCI',
  'home.trustPaymentDesc':
    'Los datos de la tarjeta se ingresan directamente en nuestro procesador de pagos y nunca pasan por nuestros servidores. El precio por lead es fijo y se muestra antes de pagar.',
  'home.trustVerifyTitle': 'Las insignias de verificación significan algo',
  'home.trustVerifyDesc':
    'Las insignias reflejan verificaciones que realmente hicimos: confirmación de correo, revisión de la cuenta y, cuando se entrega, documentación de licencia y seguro. Las cuentas sin verificar se muestran como no verificadas.',
  'home.trustSecurityTitle': 'Contacto de seguridad publicado',
  'home.trustSecurityDesc':
    'Publicamos un security.txt conforme al RFC 9116 con una dirección para reportar vulnerabilidades, para que los investigadores tengan una vía definida en lugar de adivinar.',
  'home.trustBilingualTitle': 'Plataforma totalmente bilingüe',
  'home.trustBilingualDesc':
    'Cada página, aviso y documento legal está disponible en inglés y español, porque los oficios que atiende este marketplace son bilingües.',
  'home.bandTitle': '¿Listo para hacer crecer su negocio?',
  'home.bandSub':
    'Obtenga acceso a leads de construcción residencial y comercial publicados por dueños de casa y contratistas generales en su área de servicio.',
  'home.bandPrice': 'Precio fijo de {price} por lead — sin subastas, sin recargos, sin precio por oficio',
  'home.bandCta': 'Crear Cuenta Gratis',

  'home.heroTitle': 'Encuentre trabajo de construcción en su área. Hoy.',
  'home.heroSub': 'Los dueños de casa publican proyectos. Los contratistas generales publican órdenes de trabajo. Usted desbloquea las que quiere y llama directamente al cliente.',
  'home.ctaFindWork': 'Ver Trabajos Disponibles',
  'home.ctaPostProject': 'Publicar Proyecto — Gratis',
  'home.statJobs': 'Trabajos abiertos',
  'home.statTrades': 'Oficios cubiertos',
  'home.statResponse': 'Tiempo de respuesta',
  'home.statResponseVal': 'Menos de 5 min',
  'home.pathTitle': 'Elija cómo usar la plataforma',
  'home.pathSub': 'Tres caminos simples. Sin complicaciones.',
  'home.homeownerTitle': 'Soy Dueño de Casa',
  'home.homeownerDesc': 'Publique su proyecto una vez y reciba llamadas de profesionales con licencia cerca de usted. Siempre gratis.',
  'home.gcTitle': 'Soy Contratista General',
  'home.gcDesc': 'Publique órdenes de trabajo y consiga subcontratistas que sí se presentan. Publicar es gratis.',
  'home.subTitle': 'Soy Subcontratista',
  'home.subDesc': 'Vea todos los trabajos abiertos en su área. Desbloquee los que quiera o suscríbase para recibir leads incluidos.',
  'home.getStarted': 'Comenzar',
  'home.latestJobs': 'Oportunidades abiertas',
  'home.latestJobsSub': 'Órdenes de trabajo y proyectos de dueños de casa abiertos en su región.',

  'home.howTitle': 'Cómo funciona',
  'home.how1Title': 'Cree su cuenta gratis',
  'home.how1Desc': 'Elija su rol, agregue su oficio y su área de servicio. Toma menos de 60 segundos.',
  'home.how2Title': 'Vea el trabajo',
  'home.how2Desc': 'Los trabajos disponibles aparecen apenas inicia sesión. Filtre por oficio, ciudad, presupuesto y tipo de proyecto.',
  'home.how3Title': 'Desbloquee y llame',
  'home.how3Desc': 'Pague por lead o use los leads incluidos en su plan. Recibe nombre, teléfono, correo y todos los detalles al instante.',
  'home.tradesTitle': 'Todos los oficios de la obra',
  'home.tradesSub': 'Explore trabajos abiertos por oficio y ciudad.',
  'home.faqTitle': 'Preguntas frecuentes',
  'home.faq1Q': '¿Cuánto cuesta un lead?',
  'home.faq1A': 'Cada lead cuesta los mismos $29 — el precio nunca cambia por oficio, tipo de proyecto, presupuesto ni categoría. Los miembros desbloquean leads con su asignación mensual incluida.',
  'home.faq2Q': '¿Tengo que ser miembro?',
  'home.faq2A': 'No. Puede comprar leads individuales por $29 sin membresía. Basic ($99/mes, 5 leads) y Pro ($199/mes, 10 leads) son mensuales sin compromiso. Premium cuesta $499/mes con 20 leads al mes y requiere un compromiso de 12 meses ($5,988 en total, cobrado mes a mes — no por adelantado).',


  'home.faq3Q': '¿Es gratis para los dueños de casa?',
  'home.faq3A': 'Sí. Publicar un proyecto siempre es gratis para dueños de casa, y los contratistas generales también publican órdenes de trabajo gratis.',
  'home.faq4Q': '¿La plataforma está disponible en español?',
  'home.faq4A': 'Sí. Toda la plataforma, los correos y los mensajes de texto están disponibles en inglés y español. Cambie de idioma cuando quiera desde el encabezado.',
  'home.ctaBottomTitle': 'Hay trabajo abierto en su área.',

  'home.ctaBottomSub': 'Cree su cuenta gratis de subcontratista y vea qué hay abierto en su ciudad.',
  'home.newsletterTitle': 'Reciba alertas de trabajos',
  'home.newsletterSub': 'Le enviaremos nuevas oportunidades de su oficio y su ciudad.',
  'home.newsletterOk': 'Ya está en la lista. Revise su correo para nuevos trabajos.',
  'home.smsOptIn': 'Envíenme alertas de trabajo por texto. Pueden aplicar tarifas de mensajes y datos. Responda STOP para cancelar.',
  'home.phonePlaceholder': 'Número de teléfono (opcional)',

  'jobs.title': 'Trabajos Disponibles',
  'jobs.subtitle': 'Proyectos y órdenes de trabajo abiertas de dueños de casa y contratistas generales.',
  'jobs.filterTrade': 'Oficio',
  'jobs.filterCity': 'Ciudad',
  'jobs.filterState': 'Estado',
  'jobs.filterRadius': 'Radio',
  'jobs.filterBudget': 'Presupuesto mínimo',
  'jobs.filterType': 'Tipo de proyecto',
  'jobs.results': 'trabajos encontrados',
  'jobs.empty': 'No hay proyectos que coincidan con los filtros seleccionados.',
  'jobs.loading': 'Cargando proyectos...',
  'jobs.errLoad': 'No se pudieron cargar los proyectos. Intente nuevamente.',
  'jobs.errRate': 'Demasiadas solicitudes. Espere e intente nuevamente.',
  'jobs.errRateWait': 'Demasiadas solicitudes. Intente de nuevo en {minutes} minuto(s).',
  'jobs.errNetwork': 'Revise su conexión e intente nuevamente.',
  'jobs.retry': 'Reintentar',
  'jobs.clearFilters': 'Borrar filtros',
  'jobs.unlock': 'Desbloquear Lead',
  'jobs.unlocked': 'Desbloqueado',
  'jobs.cityHidden': 'Ciudad oculta hasta la compra',
  'jobs.unlockFailed': 'No se pudo desbloquear este lead. Intente de nuevo o contacte a soporte.',

  'jobs.locationLockedTitle': 'Ubicación disponible tras comprar el lead',
  'jobs.locationLockedBody': 'Ahora puede ver el estado, la categoría, la descripción y el presupuesto. La ciudad, la dirección exacta, el código postal, los datos de contacto y los documentos del proyecto se desbloquean en cuanto compre este lead.',

  'jobs.stateOnly': 'Solo estado',
  'jobs.unlocksNow': 'Desbloqueado con su compra',
  'jobs.documents': 'Documentos del Proyecto',
  'jobs.documentsLocked': 'documentos se desbloquean tras la compra',
  'docs.count': '{n} documento(s) adjunto(s)',
  'docs.drop': 'Adjunte planos, diseños, alcance de trabajo, permisos o especificaciones en PDF',
  'docs.browse': 'Elegir archivos PDF',
  'docs.limits': 'Hasta {max} PDF, {mb} MB cada uno',
  'docs.errMax': 'Puede adjuntar hasta {max} documentos.',
  'docs.errType': 'Solo se pueden adjuntar documentos PDF.',
  'docs.errSize': 'Cada documento debe pesar menos de {mb} MB.',
  'docs.kind.plans': 'Planos',
  'docs.kind.blueprints': 'Diseños',
  'docs.kind.scope': 'Alcance de Trabajo',
  'docs.kind.permits': 'Permisos',
  'docs.kind.specs': 'Especificaciones',
  'docs.kind.other': 'Otro PDF',
  'docs.open': 'Abrir PDF',
  'docs.private': 'Almacenamiento privado. Solo compradores verificados reciben un enlace temporal.',
  'docs.errSignature': 'Ese archivo no es un PDF real y fue rechazado.',
  'docs.errLocked': 'No se pudo abrir el documento. Compre el lead para acceder.',
  'set.lockOffWarn': 'Aviso: al desactivar el bloqueo, la ciudad y el código postal quedan visibles para todos en la vista pública.',

  'form.documentsHint': 'Adjunte planos, diseños, alcance de trabajo, permisos o especificaciones en PDF. Solo los subcontratistas que compren el lead pueden abrirlos.',
  'form.errDocsRetry': 'No se pudieron adjuntar los documentos. Su proyecto se puede publicar sin archivos: quite los archivos e intente nuevamente, o vuelva a subirlos.',
  'form.errDocsMax': 'Adjuntó demasiados documentos. Quite algunos PDF e intente nuevamente.',
  'form.errDocsSize': 'Uno de los PDF supera el tamaño permitido. Suba un archivo más pequeño.',
  'form.errDocsInvalid': 'Uno de los archivos adjuntos no es válido. Quítelo y vuelva a subirlo.',
  'form.errDocsDropped': 'El proyecto se publicó, pero los documentos no se guardaron. Edite el proyecto y vuelva a subirlos.',
  'form.errPhotosMax': 'Subió demasiadas fotos. Quite algunas e intente nuevamente.',
  'admin.piiFlag': 'Datos de contacto expuestos',
  'admin.piiBody': 'Este anuncio contiene un teléfono o correo en el texto público, lo que evita el bloqueo del lead. Edítelo o retírelo.',
  'pricing.perTerm': ' / {n} meses',

  'set.title': 'Publicaciones y bloqueo de leads',
  'set.sub': 'Controle cuántos archivos puede subir un contratista y qué se oculta antes de comprar el lead.',
  'set.maxImages': 'Máximo de imágenes por proyecto',
  'set.maxDocs': 'Máximo de PDF por proyecto',
  'set.maxImageMb': 'Tamaño máximo por imagen (MB)',
  'set.maxDocMb': 'Tamaño máximo por PDF (MB)',
  'set.fileTypes': 'Tipos de archivo permitidos',
  'set.lockEnabled': 'Ocultar la ubicación hasta que se compre el lead',
  'set.lockFields': 'Campos ocultos antes de la compra',
  'set.saved': 'Configuración guardada.',
  'set.saveErr': 'No se pudo guardar. Solo los administradores pueden cambiar esto.',
  'set.dbNote': 'La ciudad y el código postal también están protegidos en la base de datos: el listado público se sirve desde una vista enmascarada y la fila completa solo la puede leer el dueño, el comprador o un administrador.',
  'admin.tabSettings': 'Configuración',

  'jobs.viewDetails': 'Ver detalles',
  'jobs.urgent': 'Urgente',
  'jobs.typicalTimeline': 'Duración típica',
  'jobs.verifiedPoster': 'Cuenta verificada',

  'jobs.postedBy': 'Publicado por',
  'jobs.byHomeowner': 'Dueño de Casa',
  'jobs.byGc': 'Contratista General',
  'jobs.distance': 'Distancia',
  'jobs.miles': 'mi',
  'jobs.leadPrice': 'Precio del lead',
  'jobs.contactLocked': 'Los datos de contacto están ocultos hasta que desbloquee este lead.',
  'jobs.contactUnlocked': 'Datos de contacto',
  'jobs.startDate': 'Inicio deseado',
  'jobs.dueDate': 'Fecha límite',
  'jobs.description': 'Descripción del proyecto',
  'jobs.similar': 'Trabajos similares cerca',
  'jobs.loginToUnlock': 'Inicie sesión como subcontratista para desbloquear este lead.',
  'jobs.notFound': 'Este trabajo ya no está disponible.',

  'auth.loginTitle': 'Inicie sesión en su cuenta',
  'auth.loginSub': 'Bienvenido de nuevo. Su trabajo lo está esperando.',
  'auth.password': 'Contraseña',
  'auth.forgot': '¿Olvidó su contraseña?',
  'auth.reset': 'Enviar enlace de recuperación',
  'auth.resetSent': 'Si ese correo existe, el enlace de recuperación va en camino.',
  'auth.noAccount': '¿No tiene cuenta?',
  'auth.haveAccount': '¿Ya tiene cuenta?',
  'auth.chooseRole': '¿Qué lo trae aquí?',
  'auth.chooseRoleSub': 'Elija una opción. Siempre puede crear otra cuenta después.',
  'auth.registerHomeowner': 'Registro de dueño de casa',
  'auth.registerGc': 'Registro de contratista general',
  'auth.registerSub': 'Registro de subcontratista',
  'auth.createAccount': 'Crear Cuenta',
  'auth.creating': 'Creando cuenta...',
  'auth.firstName': 'Nombre',
  'auth.lastName': 'Apellido',
  'auth.companyName': 'Nombre de la Empresa',
  'auth.contactName': 'Nombre de Contacto',
  'auth.ownerName': 'Nombre del Dueño',
  'auth.license': 'Número de Licencia',
  'auth.years': 'Años en el Negocio',
  'auth.employees': 'Número de Empleados',
  'auth.serviceAreas': 'Áreas de Servicio (ciudades separadas por comas)',
  'auth.tradesNeeded': 'Oficios que Necesita',
  'auth.tradeCategories': 'Categorías de Oficio',
  'auth.selectTrades': 'Seleccione todos los oficios que apliquen',
  'auth.terms': 'Al crear una cuenta usted acepta nuestros Términos de Servicio y Política de Privacidad.',
  'auth.errInvalid': 'Complete todos los campos obligatorios.',
  'auth.errEmail': 'Ingrese un correo electrónico válido.',
  'auth.errPassword': 'La contraseña debe tener al menos 6 caracteres.',
  'auth.errCreds': 'El correo o la contraseña son incorrectos.',
  'auth.errTooMany': 'Demasiados intentos. Intente de nuevo en {minutes} minuto(s).',
  'auth.errCaptcha': 'La verificación de seguridad falló. Complete la verificación e intente de nuevo.',
  'auth.captchaLabel': 'Protegido contra abuso automatizado',
  'auth.captchaWait': 'Complete la verificación de seguridad de arriba.',

  'auth.errTrades': 'Seleccione al menos un oficio.',
  'auth.preferredLanguage': 'Idioma Preferido',

  'dash.welcome': 'Bienvenido de nuevo',
  'dash.myProjects': 'Mis Proyectos',
  'dash.postProject': 'Publicar Proyecto',
  'dash.postWorkOrder': 'Publicar Orden de Trabajo',
  'dash.projectResponses': 'Respuestas de profesionales',
  'dash.noProjects': 'Todavía no ha publicado un proyecto.',
  'dash.noWorkOrders': 'Todavía no ha publicado una orden de trabajo.',
  'dash.myWorkOrders': 'Mis Órdenes de Trabajo',
  'dash.applicants': 'Solicitantes',
  'dash.availableJobs': 'Trabajos Disponibles',
  'dash.recommended': 'Recomendados para usted',
  'dash.recommendedSub': 'Según sus oficios y su área de servicio.',
  'dash.myLeads': 'Mis Leads Comprados',
  'dash.mySubscription': 'Mi Suscripción',
  'dash.noLeads': 'Todavía no ha desbloqueado leads. Desbloquee uno arriba y reciba los datos de contacto al instante.',
  'dash.noSubscription': 'Sin plan activo. Usted está en pago por lead.',
  'dash.viewPlans': 'Ver Planes',
  'leads.balanceTitle': 'Balance Mensual de Leads',
  'leads.remaining': 'Leads disponibles',
  'leads.usedOf': '{used} de {included} leads incluidos usados este mes',
  'leads.renews': 'Se renueva',
  'leads.exhausted': 'Ya usó todos los leads incluidos en su plan para este periodo. No se le entregarán más leads hasta la renovación — mejore su plan para desbloquear más ahora.',
  'leads.pastDue': 'Su último pago no se procesó, así que la entrega de leads está pausada. Actualice su método de pago para reanudarla.',
  'leads.upgrade': 'Mejorar Plan',
  'leads.explain': 'Un crédito = un lead. Su balance baja uno cada vez que desbloquea un lead. Los créditos son solo para el ciclo de facturación actual: los créditos no usados se pierden al terminar el ciclo y no se acumulan. Su siguiente asignación se agrega en cuanto se confirma el pago de la renovación — si un pago no se procesa, no se emiten créditos nuevos.',

  'leads.noneLeft': 'No quedan créditos en este ciclo. Compre un lead individual por $29 o mejore su plan.',
  'leads.extraCredits': 'Créditos de lead individual',
  'leads.extraCreditsNote': 'Comprados por separado a $29 cada uno. Se usan automáticamente cuando se acaban los créditos del plan.',
  'leads.noRollover': 'Los créditos no se acumulan — los no usados se pierden en la renovación.',
  'leads.oneCreditNote': 'Desbloquear este lead usa 1 crédito.',
  'leads.soldOutTitle': 'Agotado',
  'leads.soldOutBody': 'Este lead ya se compartió con el máximo de 6 contratistas, así que está cerrado a nuevas compras. No se cobró ningún crédito.',
  'leads.soldOutCta': 'Ver proyectos abiertos',
  'leads.soldOutFlash': 'Este lead acaba de llegar a su límite de 6 contratistas. No se usó ningún crédito.',

  'auth.newPassword': 'Nueva contraseña',
  'auth.confirmPassword': 'Confirme la nueva contraseña',
  'auth.currentPassword': 'Contraseña actual',
  'auth.changePassword': 'Cambiar Contraseña',
  'auth.passwordSaved': 'Su contraseña fue actualizada.',
  'auth.errPasswordMatch': 'Las dos contraseñas no coinciden.',
  'auth.setNewTitle': 'Elija una nueva contraseña',
  'auth.setNewSub': 'Ingrese una nueva contraseña para su cuenta.',
  'auth.setNewCta': 'Guardar nueva contraseña',
  'auth.resetExpired': 'Este enlace de recuperación no es válido o ya expiró. Solicite uno nuevo en la página de inicio de sesión.',
  'auth.backToLogin': 'Volver a iniciar sesión',
  'dash.includedLeads': 'Leads incluidos disponibles',
  'dash.needTools': '¿Necesita Más Herramientas?',
  'dash.needToolsSub': 'Mejoras opcionales. Agréguelas solo si las quiere.',
  'dash.addon.crm': 'CRM',
  'dash.addon.crmDesc': 'Contactos, embudos, seguimientos y tareas.',
  'dash.addon.crew': 'Gestión de Cuadrilla',
  'dash.addon.crewDesc': 'Lista de empleados, asignación de cuadrilla y trabajos, horarios.',
  'dash.addon.chat': 'Chat de Proyecto',
  'dash.addon.chatDesc': 'Mensajes, fotos y archivos compartidos.',
  'dash.addon.logs': 'Bitácoras Diarias',
  'dash.addon.logsDesc': 'Registros de trabajo, fotos, materiales y avance.',
  'dash.addonRequest': 'Solicitar acceso',
  'dash.addonRequested': 'Solicitud recibida. Nuestro equipo lo contactará.',
  'dash.leadStage': 'Etapa',
  'dash.stage.purchased': 'Lead Comprado',
  'dash.stage.contacted': 'Contactado',
  'dash.stage.estimate': 'Estimado Enviado',
  'dash.stage.won': 'Ganado',
  'dash.stage.lost': 'Perdido',
  'dash.settings': 'Configuración de Cuenta',

  'form.projectTitle': 'Título del Proyecto',
  'form.projectDesc': 'Descripción del Proyecto',
  'form.category': 'Categoría del Proyecto',
  'form.address': 'Dirección de la Propiedad',
  'form.budgetRange': 'Rango de Presupuesto',
  'form.startDate': 'Fecha de Inicio Deseada',
  'form.photos': 'Subir Fotos',
  'form.photosHint': 'Arrastre las fotos aquí, elija archivos o tome una foto con su teléfono. Opcional, pero recibe más respuestas.',
  'upload.drop': 'Arrastre y suelte las fotos aquí',
  'upload.or': 'o',
  'upload.browse': 'Elegir archivos',
  'upload.camera': 'Tomar foto',
  'upload.uploading': 'Subiendo...',
  'upload.remove': 'Quitar foto',
  'upload.count': '{n} de {max} fotos',
  'upload.cover': 'Portada',
  'upload.makeCover': 'Hacer portada',
  'upload.moveLeft': 'Mover a la izquierda',
  'upload.moveRight': 'Mover a la derecha',
  'dash.editProject': 'Editar',
  'form.editProject': 'Editar proyecto',
  'form.editWorkOrder': 'Editar orden de trabajo',
  'form.saveChanges': 'Guardar cambios',

  'upload.errType': 'Solo se pueden subir archivos de imagen (JPG, PNG, WEBP, HEIC).',
  'upload.errSize': 'Cada foto debe pesar menos de 10 MB.',
  'upload.errMax': 'Puede subir hasta {max} fotos.',
  'upload.errFailed': 'Esa carga no se completó. Intente de nuevo.',
  'upload.errAuth': 'Inicie sesión de nuevo para subir fotos.',
  'gallery.prev': 'Foto anterior',
  'gallery.next': 'Foto siguiente',
  'gallery.photo': 'Foto',
  'gallery.swipeHint': 'Deslice para ver más fotos',

  'form.notes': 'Notas Adicionales',
  'form.woTitle': 'Título de la Orden de Trabajo',
  'form.woDesc': 'Descripción',
  'form.tradeRequired': 'Oficio Requerido',
  'form.dueDate': 'Fecha Límite',
  'form.budget': 'Presupuesto',
  'form.documents': 'Subir Documentos',
  'form.projectType': 'Tipo de Proyecto',
  'form.publish': 'Publicar',
  'form.publishing': 'Publicando...',
  'form.successProject': 'Su proyecto está publicado. Estamos notificando a profesionales con licencia.',
  'form.successWo': 'Su orden de trabajo está activa. Estamos notificando a subcontratistas de ese oficio.',
  'form.contactSection': '¿Cómo lo pueden contactar los profesionales?',

  'pay.unlockTitle': 'Desbloquear este lead',
  'pay.unlockSub': 'Pago único. Recibe los datos completos de contacto de inmediato.',
  'pay.subscribeTitle': 'Active su plan',
  'pay.summary': 'Resumen de la orden',
  'pay.leadFor': 'Lead',
  'pay.payNow': 'Pagar Ahora',
  'pay.processing': 'Procesando...',
  'pay.success': 'Pago completo. Datos de contacto desbloqueados.',
  'pay.successSub': 'Su suscripción está activa.',
  'pay.error': 'No pudimos iniciar el pago. Intente de nuevo.',
  'pay.setup': 'Los pagos se están configurando. Vuelva a intentar en un momento.',
  'pay.useIncluded': 'Usar 1 lead incluido',
  'pay.usedIncluded': 'Lead desbloqueado con los créditos de su plan.',
  'pay.securedBy': 'Pago seguro y encriptado',
  // ── Compra de lead individual por $29 (pago con tarjeta en la página) ───
  'jobs.unlockWithCredit': 'Desbloquear con 1 Crédito',
  // ── Regreso del pago: activación automática de créditos ────────────────
  'payret.title': 'Pago completado',
  'payret.paid': 'Pago exitoso',
  'payret.activating': 'Pago recibido. Estamos activando sus créditos…',
  'payret.waitBody': 'Esto toma unos segundos. No cierre esta página — lo llevaremos directo a los Proyectos.',
  'payret.stillWorking': 'Todavía trabajando. Confirmamos su pago con el procesador antes de entregar los créditos.',
  'payret.creditReady': 'crédito de lead listo para usar',
  'payret.creditsReady': 'créditos de leads listos para usar',
  'payret.sending': 'Lo estamos llevando a los Proyectos disponibles…',
  'payret.browse': 'Ver Proyectos',
  'payret.signinBody': 'Su pago está confirmado. Inicie sesión con el correo que usó para pagar y sus créditos se aplicarán automáticamente.',
  'payret.signin': 'Iniciar sesión y recibir mis créditos',
  'payret.thenProjects': 'Después continuamos automáticamente a los Proyectos.',
  'payret.signupBody': 'Su pago está confirmado. Cree su contraseña para activar la cuenta y sus créditos se aplicarán automáticamente.',
  'payret.activateAccount': 'Activar mi cuenta y recibir mis créditos',
  'payret.slowTitle': 'Su pago está seguro',
  'payret.slowBody': 'La confirmación del procesador está tardando más de lo normal. Sus créditos se entregarán en cuanto llegue — no se le cobrará de nuevo. Puede volver a revisar o escribirnos.',
  'payret.checkAgain': 'Revisar de nuevo',
  'payret.webhookNote': 'Los créditos se entregan únicamente cuando se verifica el pago con el procesador, no porque el navegador llegue a esta página.',
  'payret.dashboard': 'Ir a mi panel',
  'tour.replay': 'Ver la guía rápida',
  'tour.replayNote': '5 pasos · cómo usar sus créditos y contactar al cliente',

  'pay.buyThisLead': 'Comprar Este Lead',

  'pay.buyThisLeadNote': 'Pago único de $29. Recibe 1 crédito y este lead se desbloquea de inmediato.',
  'pay.processingPayment': 'Procesando el pago…',
  'pay.unlockingLead': 'Pago aprobado — desbloqueando su lead…',
  'pay.openingCheckout': 'Abriendo el pago seguro…',
  'pay.loadingForm': 'Cargando el formulario de pago seguro…',
  'pay.formNotReady': 'El formulario de pago aún se está cargando. Toque Pagar de nuevo en un momento.',
  'pay.intentFailed': 'No pudimos iniciar el pago. Intente de nuevo.',
  'pay.declined': 'Su tarjeta fue rechazada. Pruebe otra tarjeta o comuníquese con su banco.',
  'pay.notCompleted': 'El pago no se completó. No se cobró nada.',
  'pay.timeout': 'El pago está tardando demasiado. No se ha cobrado dos veces — toque Reintentar.',
  'pay.networkError': 'Problema de conexión. No hubo cobro doble — toque Reintentar.',
  'pay.settleRetry': 'Su pago se procesó. Todavía estamos desbloqueando el lead — toque Reintentar o recargue la página.',
  'pay.noChargeOnDecline': 'Un pago rechazado no genera cobro, ni crédito, ni acceso al lead.',
  'pay.retry': 'Reintentar el pago',
  'pay.tryAgain': 'El pago no se completó. Puede intentarlo de nuevo.',
  'pay.alreadyOwned': 'Usted ya tiene acceso a este lead — los datos de contacto están abajo.',
  'pay.soldOutNoCharge': 'Este lead acaba de llegar a su límite de 6 contratistas, así que no se puede comprar. No se le cobró nada.',
  'pay.successTitle': 'Pago aprobado — lead desbloqueado',
  'pay.successBody':
    'Su pago de $29 emitió 1 crédito y ese crédito desbloqueó este lead. El nombre de contacto, el teléfono, el correo y la dirección exacta están aquí mismo, y esta compra ocupó uno de los 6 lugares de contratista.',
  // Compra de $29 = 1 crédito en el saldo. Comprar NO es gastar: el crédito se
  // gasta cuando el contratista pulsa Desbloquear, igual que los créditos de
  // membresía (Básico 5 / Pro 10 / Premium 20 / Compra individual 1).
  'pay.creditedTitle': 'Pago confirmado — 1 crédito agregado a su saldo',
  'pay.creditedBody':
    'Su pago de $29 agregó 1 crédito a su cuenta. El crédito es suyo hasta que decida usarlo: sirve para cualquier proyecto abierto, no solo para este. Nada se desbloqueó todavía.',
  'pay.creditsInBalance': 'Créditos disponibles',
  'pay.unlockNowCta': 'Desbloquear este proyecto (1 crédito)',
  'pay.saveCreditCta': 'Guardar el crédito y ver más proyectos',
  'pay.addingCredit': 'Pago aprobado — agregando su crédito…',
  'pay.creditNote': 'Pago único de $29. Recibe 1 crédito y usted decide en qué proyecto usarlo.',


  'pay.continueCheckout': 'Continuar al pago seguro',
  'pay.pendingTitle': 'Complete su pago en la ventana segura',
  'pay.pendingBody':
    'Abrimos el pago seguro en una pestaña nueva. Su plan y sus leads incluidos se activan automáticamente en cuanto confirmemos el pago — normalmente en menos de un minuto. Puede cerrar esta ventana.',
  'pay.reopenCheckout': 'Volver a abrir el pago seguro',
  'pay.seePlans': 'Ver planes',
  'pay.leadNeedsPlan':
    'Los leads se desbloquean con los leads incluidos en su plan mensual. Elija un plan para empezar.',
  'pay.activationPending':
    'Estamos confirmando su pago. Su plan se activará automáticamente en cuanto se confirme.',
  'pay.activated': 'Pago confirmado. Su plan está activo.',
  'pay.contactUs': 'Contáctenos',



  'pricing.title': 'Precios simples para subcontratistas',
  'pricing.sub': 'Pague solo por el trabajo que quiere, o hágase miembro y reciba leads incluidos.',
  'pricing.payPerLead': 'Compra de Lead Individual',
  'pricing.payPerLeadDesc': 'Sin membresía. Desbloquee cualquier lead por un precio único.',
  'pricing.payPerLeadPrice': '$29 por lead',
  'pricing.perLead': 'por lead',
  'pricing.fixedLeadNote': 'Cada lead cuesta $29 — el precio nunca cambia por oficio, tipo de proyecto, presupuesto ni categoría.',
  'pricing.plans': 'Planes de membresía',
  'pricing.perMonth': '/mes',
  'pricing.choosePlan': 'Elegir plan',
  'pricing.currentPlan': 'Plan actual',
  'pricing.howLeadsWork': 'Cómo funcionan los leads',
  'pricing.howLeads1': 'Cada lead individual del mercado cuesta los mismos $29. Sin precios variables, sin leads premium, sin precios por categoría.',
  'pricing.howLeads2': 'Si su membresía tiene leads incluidos disponibles, desbloquear un lead descuenta uno de su balance en lugar de cobrarle.',
  'pricing.howLeads3': 'Cuando se acaben sus leads incluidos puede seguir trabajando y desbloquear cualquier lead por $29, o mejorar su membresía cuando quiera.',
  'pricing.contactSupport': 'Contactar a soporte sobre un lead',
  'pricing.leadPriceTable': 'Precio de los leads',
  'pricing.tradeCol': 'Oficio',
  'pricing.resCol': 'Residencial',
  'pricing.comCol': 'Comercial',
  'pricing.freeNote': 'Los dueños de casa y los contratistas generales publican gratis. Siempre.',

  // --- Acciones flotantes de conversión (CTA Comenzar + WhatsApp) ---
  'cta.startNow': 'Comenzar',
  'cta.getLeads': 'Obtener Leads',
  'cta.viewPlans': 'Ver Planes',
  'cta.getStarted': 'Empezar Ahora',
  'cta.whatsapp': 'WhatsApp',
  'cta.whatsappAria': 'Escríbanos por WhatsApp',
  'cta.plansTitle': 'Empiece a recibir leads hoy',
  'cta.plansSub': 'Compre un lead o hágase miembro. Cada lead cuesta el mismo precio fijo.',
  'cta.plansHeading': 'Planes de membresía',
  'cta.singleLead': 'Lead Individual',
  'cta.singleLeadDesc': 'Sin membresía. Compre un crédito y desbloquee cualquier proyecto abierto.',
  'cta.oneCredit': '1 crédito',
  'cta.creditsPerCycle': '{n} créditos de lead por ciclo de facturación',
  'cta.cancelAnytime': 'Mensual — cancele cuando quiera.',
  'cta.creditRules':
    '1 crédito desbloquea 1 lead. Los créditos se emiten cuando se confirma cada pago y no se acumulan — los no usados se pierden al terminar el ciclo. Cada lead se comparte con un máximo de 6 contratistas.',

  'cta.compareAll': 'Comparar todos los planes y beneficios',
  'cta.questions': '¿Preguntas antes de comprar?',
  'cta.chatNow': 'Escríbanos por WhatsApp',
  'cta.replyFast': 'Normalmente respondemos en minutos en horario laboral.',


  'status.active': 'Activo',
  'status.removed': 'Retirado',
  'status.paused': 'Pausado',



  'admin.title': 'Panel Ejecutivo',
  'admin.users': 'Usuarios',
  'admin.homeowners': 'Dueños de Casa',
  'admin.gcs': 'Contratistas Generales',
  'admin.subs': 'Subcontratistas',
  'admin.projects': 'Proyectos',
  'admin.workOrders': 'Órdenes de Trabajo',
  'admin.leadsSold': 'Leads Vendidos',
  'admin.revenue': 'Ingresos Totales',
  'admin.mrr': 'Ingreso Mensual Recurrente',
  'admin.conversion': 'Tasa de conversión de leads',
  'admin.topTrades': 'Oficios con mejor desempeño',
  'admin.geo': 'Desglose geográfico',
  'admin.manageUsers': 'Administrar usuarios',
  'admin.managePricing': 'Precios de leads',
  'admin.approve': 'Aprobar',
  'admin.suspend': 'Suspender',
  'admin.suspended': 'Suspendido',
  'admin.approved': 'Aprobado',
  'admin.pending': 'Pendiente',
  'admin.activity': 'Registro de actividad',
  'admin.noAccess': 'Esta área está reservada para administradores de la plataforma.',
  'admin.savePrice': 'Guardar precios',
  'admin.priceSaved': 'Precios actualizados.',

  'nav.contact': 'Contacto',
  'contact.title': 'Contacto y Soporte',
  'contact.sub': 'Envíenos un mensaje y nuestro equipo bilingüe le responde el mismo día hábil.',
  'contact.formTitle': 'Envíenos un mensaje',
  'contact.subject': 'Asunto',
  'contact.subject.general': 'Pregunta general',
  'contact.subject.billing': 'Facturación y pagos',
  'contact.subject.support': 'Soporte técnico',
  'contact.subject.report': 'Reportar una publicación',
  'contact.message': 'Mensaje',
  'contact.messagePlaceholder': 'Cuéntenos en qué podemos ayudarle...',
  'contact.send': 'Enviar mensaje',
  'contact.sending': 'Enviando...',
  'contact.successTitle': 'Mensaje recibido',
  'contact.successBody': 'Gracias por escribirnos. Nuestro equipo le responderá por correo dentro de un día hábil.',
  'contact.sendAnother': 'Enviar otro mensaje',
  'contact.errMessage': 'Escriba un mensaje de al menos 10 caracteres.',
  'contact.errSave': 'No pudimos enviar su mensaje. Intente de nuevo.',
  'contact.reachUs': 'Otras formas de contactarnos',
  'contact.emailUs': 'Escríbanos',
  'contact.callUs': 'Llámenos',
  'contact.hours': 'Horario de atención',
  'contact.hoursVal': 'Lunes a viernes, 7:00 a.m. a 6:00 p.m. (hora del Pacífico)',
  'contact.responseTime': 'Respuesta típica en menos de 1 día hábil',
  'contact.faqLink': 'Antes de escribir, revise nuestras preguntas frecuentes.',
  'contact.privacyNote': 'Usamos sus datos únicamente para responder su mensaje. Lea nuestra Política de Privacidad.',
  'admin.inbox': 'Bandeja de Contacto',
  'admin.inboxEmpty': 'No hay mensajes todavía.',
  'admin.markResolved': 'Marcar resuelto',
  'admin.reopen': 'Reabrir',
  'admin.new': 'Nuevo',
  'admin.resolved': 'Resuelto',
  'admin.filterAll': 'Todos',
  'admin.filterNew': 'Sin resolver',
  'admin.replyEmail': 'Responder por correo',
  'admin.tabOverview': 'Resumen',
  'admin.tabInbox': 'Bandeja',
  'admin.tabListings': 'Publicaciones e Imágenes',
  'admin.tabRoles': 'Auditoría de roles',
  'admin.tabPayments': 'Pagos',
  'admin.tabSecurity': 'Seguridad',
  'sec.humanCheck': 'Verificación rápida de seguridad — la validamos en nuestros servidores, nunca solo en su navegador.',
  'sec.botFailed': 'No pudimos verificar que es una persona. Complete la verificación de seguridad e intente de nuevo.',
  'sec.rateLimited': 'Demasiados intentos. Espere unos minutos e intente de nuevo.',
  'sec.consentMarketing': 'Acepto que me contacten sobre mi solicitud y acepto la',
  'sec.title': 'Seguridad y captura de leads',
  'sec.sub': 'Verificación anti-bots, límites de solicitudes, registro de auditoría, visitantes anónimos y la cola de reintentos del CRM.',
  'sec.refresh': 'Actualizar',
  'sec.visitors': 'Visitantes anónimos',
  'sec.identified': 'Identificados (dieron sus datos)',
  'sec.blocked24': 'Bloqueos / alertas (24 h)',
  'sec.queuePending': 'Reintentos de CRM pendientes',
  'sec.queueDead': 'CRM sin entregar',
  'sec.eventsTitle': 'Registro de auditoría de seguridad',
  'sec.eventsSub': 'Retos anti-bot, bloqueos por límite, resultados de sincronización del CRM y fallos de permisos. Nunca se registran contraseñas, datos de tarjeta ni secretos.',
  'sec.queueTitle': 'Cola de reintentos del CRM',
  'sec.queueSub': 'Todo lead que GoHighLevel no pudo aceptar queda aquí y se reintenta automáticamente con espera exponencial cada 5 minutos.',
  'sec.visitorsTitle': 'Visitantes anónimos recientes',
  'sec.visitorsSub': 'Solo un identificador propio — sin huella digital del navegador. Los datos de contacto aparecen únicamente cuando la persona envía un formulario.',
  'sec.event': 'Evento',
  'sec.severity': 'Severidad',
  'sec.detail': 'Detalle',
  'sec.when': 'Cuándo',
  'sec.attempts': 'Intentos',
  'sec.lastError': 'Último error',
  'sec.landing': 'Página de entrada',
  'sec.source': 'Origen',
  'sec.pages': 'Páginas',
  'sec.retryNow': 'Ejecutar reintentos ahora',
  'sec.retryDone': 'Reintentos completados',
  'sec.noEvents': 'Todavía no hay eventos registrados.',

  'admin.payTitle': 'Enlaces de pago de los planes',
  'admin.paySub': 'Estado en vivo de cada enlace de pago. Verde significa que los subcontratistas pueden suscribirse a ese plan ahora mismo.',
  'admin.payRecheck': 'Verificar enlaces',
  'admin.payChecking': 'Verificando enlaces...',
  'admin.payReady': 'Pago listo',
  'admin.payMissing': 'Falta el enlace de pago',
  'admin.payOpenLink': 'Abrir página de pago',
  'admin.paySecret': 'Secreto',
  'admin.payAllReady': 'Todos los enlaces de pago están configurados. Cada plan se puede comprar.',
  'admin.paySomeMissing': 'Faltan {n} enlace(s) de pago. Esos botones no pueden cobrar hasta que se configure el secreto.',
  'admin.payFailTitle': 'Intentos de suscripción bloqueados',
  'admin.payFailSub': 'Subcontratistas que intentaron suscribirse cuando el enlace de pago no estaba disponible. Contáctelos para no perder la venta.',
  'admin.payFailEmpty': 'No hay intentos bloqueados. Cada solicitud de pago llegó a una página de pago.',
  'admin.payFailReason': 'Motivo',
  'admin.payFailPlan': 'Plan',
  'admin.payFailWhen': 'Intento',
  'admin.payReason.checkout_link_not_set': 'Enlace de pago no configurado',
  'admin.payReason.unknown_plan': 'Plan desconocido',
  'admin.payReason.error': 'Error del servicio de pago',
  'admin.payFollowUp': 'Escribir al contratista',
  'admin.payResolve': 'Marcar contactado',
  'admin.payResolved': 'Contactado',
  'admin.payFailOpen': 'Requiere seguimiento',

  'admin.moderationTitle': 'Moderación de publicaciones e imágenes',
  'admin.moderationSub': 'Quite una foto individual o retire una publicación. Cada acción queda registrada.',
  'admin.modSearch': 'Buscar por título, ciudad, estado, oficio o correo del autor',
  'admin.modFilterAll': 'Todas',
  'admin.modFilterPublished': 'Publicadas',
  'admin.modFilterRemoved': 'Retiradas',
  'admin.modSelectAll': 'Seleccionar todas las mostradas',
  'admin.modSelected': '{n} seleccionadas',
  'admin.modBulkUnpublish': 'Retirar seleccionadas',
  'admin.modBulkDone': '{n} publicación(es) retiradas del inicio y del marketplace.',
  'admin.modOwner': 'Publicado por',
  'admin.modNoMatch': 'Ninguna publicación coincide con esa búsqueda.',
  'admin.modHint':
    'Al retirar una publicación desaparece de inmediato del inicio, del marketplace y del buscador. No se borra nada: puede restaurarla aquí cuando quiera.',
  'admin.modShowing': 'Mostrando {n} de {total}',
  // Etiqueta del estado en la tarjeta (singular, a diferencia del filtro plural).
  'admin.modStatusPublished': 'Publicada',
  'admin.modStatusRemoved': 'Retirada',


  'admin.photos': 'fotos',
  'admin.unpublish': 'Retirar',
  'admin.restoreListing': 'Restaurar',
  'admin.removePhoto': 'Quitar foto',
  'admin.roleAuditTitle': 'Auditoría de roles',
  'admin.roleAuditSub': 'Todas las cuentas agrupadas por tipo. Corregir un rol actualiza el perfil y los datos de acceso.',
  'admin.makeRole': 'Cambiar a',
  'admin.roleFixed': 'Rol actualizado.',
  'admin.roleFixError': 'No se pudo actualizar ese rol.',
  'admin.createSubTitle': 'Crear cuenta de subcontratista',
  'admin.createSubSub': 'Crea un acceso real de subcontratista y su perfil en el marketplace. Solo los administradores pueden usar este formulario.',
  'admin.createSubBtn': 'Crear subcontratista',
  'admin.createSubCreating': 'Creando cuenta...',
  'admin.createSubOk': 'Cuenta de subcontratista creada para {email}.',
  'admin.createSubPasswordHint': 'Mínimo 10 caracteres. Compártala de forma segura con el titular de la cuenta.',
  'admin.createSubErrExists': 'Ya existe una cuenta con ese correo.',
  'admin.createSubErrPassword': 'La contraseña debe tener al menos 10 caracteres.',
  'admin.createSubErrForbidden': 'Solo los administradores pueden crear cuentas aquí.',
  'admin.createSubErrGeneric': 'No se pudo crear la cuenta. Intente de nuevo.',

  'photos.cover': 'Portada',
  'cover.category': 'Foto de categoría',
  'cover.categoryNote':
    'El dueño de casa no subió fotos de este proyecto. La imagen mostrada es una foto de referencia de la categoría, no del proyecto real.',

  'role.homeowner': 'Dueño de Casa',
  'role.gc': 'Contratista General',
  'role.subcontractor': 'Subcontratista',
  'verify.verified': 'Verificado',
  'verify.licensed': 'Con licencia',
  'verify.insured': 'Asegurado',
  'home.tradesContact': '¿No ve su oficio? Escríbanos a',
  'dash.responsesTitle': 'Quién respondió a sus proyectos',
  'dash.responsesSub': 'Profesionales que abrieron su proyecto y pueden contactarlo.',
  'dash.noResponses': 'Todavía no ha respondido ningún profesional. Aparecerán aquí en cuanto lo hagan.',
  'dash.contractorInterested': 'Profesional interesado',
  'dash.respondedOn': 'Respondió',

  'footer.company': 'Compañía',
  'footer.about': 'Acerca de',
  'footer.contact': 'Contacto',
  'footer.aboutUs': 'Acerca de Nosotros',
  'footer.contactUs': 'Contáctenos',
  'footer.trustNav': 'Compañía y legal',
  'footer.emailUs': 'Correo de la empresa',
  'footer.address': 'Dirección comercial',
  'contact.mailingAddress': 'Dirección comercial y postal',
  'contact.addressNote': 'Dirección comercial registrada. El correo enviado aquí llega al equipo de GigGone.',

  'seo.aboutContactT': 'Información de la empresa y contacto',
  'seo.aboutContactB':
    'GigGone Marketplace opera el marketplace de construcción giggone.com. Para cualquier pregunta sobre la plataforma, su cuenta, facturación, una publicación o una solicitud de privacidad, escriba a nuestro equipo — respondemos en un día hábil.',
  'seo.aboutTrustT': 'Políticas y páginas de la empresa',


  'footer.resources': 'Recursos',
  'footer.forHomeowners': 'Para Dueños de Casa',
  'footer.forGcs': 'Para Contratistas Generales',
  'footer.forSubs': 'Para Subcontratistas',
  'footer.legal': 'Legal',
  'footer.privacy': 'Política de Privacidad',
  'footer.terms': 'Términos de Servicio',
  'footer.cookies': 'Política de Cookies',
  'footer.rights': 'Todos los derechos reservados.',
  'footer.builtFor': 'Oficios con licencia. Trabajo real. Sin ruido.',
  'cookie.text': 'Usamos cookies para mantener su sesión, recordar su idioma y medir el desempeño de la plataforma.',
  'cookie.accept': 'Aceptar',
  'cookie.decline': 'Rechazar',
  'legal.privacyTitle': 'Política de Privacidad',
  'legal.termsTitle': 'Términos de Servicio',
  'legal.updated': 'Última actualización',
};

export const dictionaries: Record<Lang, Dict> = { en, es };
